import { notify, getApiErrorMessage } from '@/lib/toast';
import { Dropdown } from 'primereact/dropdown';
import { useAuthStore } from '@/stores/auth.store';
import { Eye, EyeOff, User, Building2, Shield, ShieldCheck, Palette, FlaskConical, CheckCircle2, Zap, ExternalLink, CreditCard, Users, BookOpen, ShoppingCart, Package, Boxes, Truck, Receipt, BarChart3, FileText, Percent, Tag, MapPin, ArrowLeftRight, Landmark, ScanBarcode, Code2, Database, Lock, MessageCircle, ChevronRight, ChevronDown, Trash2, Bell, Key, ShoppingBag, Copy, CheckCheck, DollarSign, Mail, Phone, Globe, Wifi, HardDrive, Download, Upload, TableIcon, AlertCircle, Loader2, Webhook, Plus, UtensilsCrossed } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { StatCard } from '@/components/ui/stat-card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LogoUploadField } from './LogoUploadField';
import { AppearanceCard } from './AppearanceCard';
import { Checkbox } from '@/components/ui/checkbox';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { cn, formatCurrency, humanizeEnum } from '@/lib/utils';
import { formatSellerNtn } from '@/lib/ntn';
import { ReceiptPreview } from '@/components/settings/ReceiptPreview';
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { RECEIPT_LAYOUT_DEFAULTS, resolveReceiptLayout, type ReceiptLayout } from '@/types/pos';
import { PageHeader } from '@/components/ui/page-header';
import { TabBar } from '@/components/ui/tab-bar';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { applyTenantUpdate } from '@/lib/apply-tenant-update';
import toast from 'react-hot-toast';
import { useState, useEffect, useRef } from 'react';
import { CurrenciesPage } from './CurrenciesPage';
import { SettingsHistoryCard } from './SettingsHistoryCard';
import { MyPlanUsageCard } from './MyPlanUsageCard';
import { useTranslation, Trans } from 'react-i18next';
import { PERMISSION_LABELS } from '@/lib/permission-labels';
import { passwordProblem } from '@/lib/password-rules';
import { BRAND_PRESETS, brandContrastOn, isHexColor, sidebarBackgroundHex, weakModes } from '@/lib/brand-contrast';
import { getApiErrorStatus, getApiRetryAfterSeconds } from '@/lib/api-error';
import { ThrottleNotice } from '@/components/auth/ThrottleNotice';

// POS-I220: the on/off choices of Show on receipt, in the order they are listed (each key is also its label key).
const RECEIPT_SHOW_FLAGS = [
  'showLogo',
  'showTagline',
  'showAddress',
  'showPhone',
  'showNtn',
  'showCashier',
  'showCustomer',
  'showTaxBreakdown',
  'showLoyalty',
  'showFbrQr',
] as const;

/** A receipt layout differs from the saved one (texts compared after the trim the server applies). */
function receiptLayoutDirty(a: ReceiptLayout, b: ReceiptLayout): boolean {
  return (
    RECEIPT_SHOW_FLAGS.some((k) => a[k] !== b[k]) ||
    a.headerNote.trim() !== b.headerNote.trim() ||
    a.returnPolicy.trim() !== b.returnPolicy.trim()
  );
}

const PAYMENT_METHODS = ['CASH', 'CARD', 'MOBILE_WALLET', 'BANK_TRANSFER', 'CREDIT'] as const;
/** POS-I297: the codes are shown through these labels (en/ur/ro), never raw. Literal keys so the key-usage spec checks them. */
const PAYMENT_METHOD_LABEL_KEYS: Record<(typeof PAYMENT_METHODS)[number], string> = {
  CASH: 'pmCash',
  CARD: 'pmCard',
  MOBILE_WALLET: 'pmMobileWallet',
  BANK_TRANSFER: 'pmBankTransfer',
  CREDIT: 'pmCredit',
};
type PaymentMethod = typeof PAYMENT_METHODS[number];

function fmtBytes(bytes: number): string {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

interface PaymentMethodTaxRule {
  rate: number;
  name: string;
  overridesProductTax: boolean;
}

interface TerminalConfig {
  configured?: boolean;
  provider?: string;
  terminalId?: string;
  ipAddress?: string;
  port?: string;
  merchantId?: string;
}

interface TerminalForm {
  provider: string;
  terminalId: string;
  apiKey: string;
  ipAddress: string;
  port: string;
  merchantId: string;
}

interface TerminalTestResult {
  success: boolean;
  message: string;
}

interface EmailCommForm {
  smtpHost: string;
  smtpPort: number;
  smtpSecure: boolean;
  smtpUser: string;
  smtpPass: string;
  fromEmail: string;
}

interface SmsCommForm {
  provider: string;
  accountSid: string;
  authToken: string;
  fromNumber: string;
  webhookUrl: string;
}

interface WhatsappCommForm {
  useApi: boolean;
  fromNumber: string;
}

interface CommSettingsPatch {
  email?: Partial<EmailCommForm> & { enabled?: boolean };
  sms?: Partial<SmsCommForm> & { enabled?: boolean };
  whatsapp?: Partial<WhatsappCommForm> & { enabled?: boolean };
}

interface TaxRuleSettingsPatch {
  paymentMethodTaxRules?: Record<string, PaymentMethodTaxRule> | null;
  atlEnabled?: boolean;
  defaultBuyerAtlStatus?: boolean;
  furtherTaxRate?: number;
}

interface ReportSchedule {
  id: string;
  label: string;
  enabled: boolean;
  frequency: 'daily' | 'weekly' | 'monthly';
  sendAt?: string | null;
  dayOfWeek?: number | null;
  dayOfMonth?: number | null;
  recipients?: string[] | null;
  lastSentAt?: string | null;
}

interface ApiKeyRow {
  id: string;
  name: string;
  description?: string | null;
  keyPrefix: string;
  isActive: boolean;
}

interface WebhookRow {
  id: string;
  name: string;
  url: string;
  events: string[];
  isActive: boolean;
}

interface DataStatRow {
  tableName: string;
  label: string;
  tenantRows: number;
  estimatedTenantBytes: number;
  estimatedTenantSizePretty: string;
}

const WHATSAPP_NUMBER ='923330950000';
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Hi! I'd like to learn more about upgrading my POS subscription.")}`;

const ALL_FEATURES = [
  { key: 'pos',        name: 'POS & Sales',               desc: 'Point of sale, checkout, and receipt printing',          icon: ShoppingCart,   permission: 'pos:*' },
  { key: 'products',   name: 'Product Catalog',            desc: 'Products, categories, variants and tax management',      icon: Package,        permission: 'products:*' },
  { key: 'customers',  name: 'Customer Management',        desc: 'Customer records, NTN/CNIC, purchase history',           icon: Users,          permission: 'customers:*' },
  { key: 'inventory',  name: 'Inventory Control',          desc: 'Stock tracking, adjustments, low-stock alerts',          icon: Boxes,          permission: 'inventory:*' },
  { key: 'purchases',  name: 'Purchases & Suppliers',      desc: 'Purchase orders, receiving, supplier management',        icon: Truck,          permission: 'purchases:*' },
  { key: 'expenses',   name: 'Expense Tracking',           desc: 'Business expenses, categories, journal entries',         icon: Receipt,        permission: 'expenses:*' },
  { key: 'accounting', name: 'Accounting & Reports',       desc: 'Double-entry GL, P&L, balance sheet, trial balance',     icon: BarChart3,      permission: 'accounting:*' },
  { key: 'fbr',        name: 'FBR Digital Invoicing',      desc: 'Real-time FBR submission — mandatory for GST registrants',icon: FileText,      permission: 'fbr:*' },
  { key: 'tax_rates',  name: 'Tax Rate Management',        desc: 'Custom tax rates, inclusive/exclusive, per product',     icon: Percent,        permission: 'tax-rates:*' },
  { key: 'promotions', name: 'Promotions & Discounts',     desc: 'Promo codes, percentage and fixed discount rules',       icon: Tag,            permission: 'promotions:*' },
  { key: 'locations',  name: 'Multi-Location / Warehouses',desc: 'Multiple stores and warehouse locations',                icon: MapPin,         permission: 'locations:*' },
  { key: 'transfers',  name: 'Stock Transfers',            desc: 'Move inventory between locations seamlessly',            icon: ArrowLeftRight, permission: 'transfers:*' },
  { key: 'bank',       name: 'Bank Accounts & Reconciliation', desc: 'Bank management, vendor payments, reconciliation',   icon: Landmark,       permission: 'bank:*' },
  { key: 'barcode',    name: 'Barcode Label Printing',     desc: 'Print product labels for any barcode printer',           icon: ScanBarcode,    permission: 'barcode-labels:*' },
  { key: 'api',        name: 'API Access',                 desc: 'REST API for third-party integrations',                  icon: Code2,          permission: 'api:*' },
  { key: 'dedicated_database', name: 'Dedicated Database', desc: 'Isolated DB with daily backups and faster queries',      icon: Database,       permission: null, addonKey: 'dedicated_database' },
] as const;

const TERMINAL_PROVIDERS = ['MOCK', 'INGENICO', 'VERIFONE', 'PAX', 'SUMUP', 'STRIPE_TERMINAL'] as const;

function TerminalSettingsCard() {
  const [form, setForm] = useState<TerminalForm>({ provider: 'MOCK', terminalId: '', apiKey: '', ipAddress: '', port: '', merchantId: '' });
  const [testResult, setTestResult] = useState<TerminalTestResult | null>(null);
  const { t: tt } = useTranslation('settings');
  // T-525: the callback endpoint (same origin as the app's API).
  const callbackBase = `${window.location.origin}/api/v1/payment-terminal/callback`;

  const { data: config, isLoading } = useQuery({
    queryKey: ['terminal-config'],
    queryFn: () => api.get<TerminalConfig>('/payment-terminal/config').then((r) => r.data),
  });

  const qc = useQueryClient();

  const saveMutation = useMutation({
    mutationFn: (d: TerminalForm) => api.put('/payment-terminal/config', d),
    onSuccess: () => { toast.success(tt('termSavedToast')); qc.invalidateQueries({ queryKey: ['terminal-config'] }); },
    onError: (err) => notify.error(err, 'Failed to save'),
  });

  const clearMutation = useMutation({
    mutationFn: () => api.delete('/payment-terminal/config'),
    onSuccess: () => { toast.success(tt('termClearedToast')); qc.invalidateQueries({ queryKey: ['terminal-config'] }); },
  });

  const testMutation = useMutation({
    mutationFn: () => api.get<TerminalTestResult>('/payment-terminal/test-connection').then((r) => r.data),
    onSuccess: (d: TerminalTestResult) => setTestResult(d),
    onError: () => setTestResult({ success: false, message: 'Connection test failed' }),
  });

  useEffect(() => {
    if (config && config.configured) {
      setForm((prev) => ({
        ...prev,
        provider: config.provider ?? 'MOCK',
        terminalId: config.terminalId ?? '',
        ipAddress: config.ipAddress ?? '',
        port: config.port ?? '',
        merchantId: config.merchantId ?? '',
      }));
    }
  }, [config]);

  return (
    <Card className="animate-fade-in-up stagger-5 hover:shadow-glow-sm transition-all duration-200">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <CreditCard className="h-5 w-5 text-info-text" />
          {tt('termCardTitle')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {tt('termCardIntro')}
        </p>
        {isLoading ? (
          <div className="h-8 bg-muted rounded animate-pulse" />
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-1.5 block text-sm">{tt('termProviderLabel')}</Label>
                <Dropdown
                  className="w-full"
                  value={form.provider}
                  onChange={(e) => setForm({ ...form, provider: e.value })}
                  options={TERMINAL_PROVIDERS.map((p) => ({
                    label: p === 'MOCK' ? tt('termDemoProvider') : p.replace(/_/g, ' '),
                    value: p,
                  }))}
                  optionLabel="label"
                  optionValue="value"
                />
              </div>
              <div>
                <Label className="mb-1.5 block text-sm">{tt('termIdLabel')}</Label>
                <Input maxLength={64} placeholder="TID001" value={form.terminalId} onChange={(e) => setForm({ ...form, terminalId: e.target.value })} />
              </div>
              <div>
                <Label className="mb-1.5 block text-sm">{tt('termIpLabel')}</Label>
                <Input maxLength={253} placeholder="192.168.1.50" value={form.ipAddress} onChange={(e) => setForm({ ...form, ipAddress: e.target.value })} />
              </div>
              <div>
                <Label className="mb-1.5 block text-sm">{tt('termPortLabel')}</Label>
                <Input maxLength={5} inputMode="numeric" placeholder="8080" value={form.port} onChange={(e) => setForm({ ...form, port: e.target.value })} />
              </div>
              <div>
                <Label className="mb-1.5 block text-sm">{tt('termApiKeyLabel')}</Label>
                <Input type="password" placeholder="sk_..." value={form.apiKey} onChange={(e) => setForm({ ...form, apiKey: e.target.value })} />
              </div>
              <div>
                <Label className="mb-1.5 block text-sm">{tt('termMerchantIdLabel')}</Label>
                <Input maxLength={64} placeholder="MID123" value={form.merchantId} onChange={(e) => setForm({ ...form, merchantId: e.target.value })} />
              </div>
            </div>

            {/* T-525: where the terminal posts its result. The token is signed per payment, so the
                address to allow-list is the base URL; the full signed URL travels with each request. */}
            <div className="rounded-lg border p-3 space-y-1.5 text-sm">
              <p className="font-medium">{tt('terminalCallbackTitle')}</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 truncate rounded bg-muted px-2 py-1 text-xs">{callbackBase}</code>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { navigator.clipboard.writeText(callbackBase); toast.success(tt('terminalCallbackCopied')); }}
                >
                  <Copy className="h-3.5 w-3.5 me-1" /> {tt('terminalCallbackCopy')}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">{tt('terminalCallbackHint')}</p>
              <p className="text-xs text-muted-foreground font-mono">{callbackBase}?tenantId=…&amp;token=…</p>
            </div>

            {testResult && (
              <div className={`rounded-lg px-3 py-2 text-sm flex items-center gap-2 ${testResult.success ? 'bg-success/10 text-success-text' : 'bg-destructive/10 text-destructive-text'}`}>
                {testResult.success ? '✅' : '❌'} {testResult.message}
              </div>
            )}

            <div className="flex gap-2 flex-wrap">
              <Button size="sm" onClick={() => saveMutation.mutate(form)} disabled={saveMutation.isPending}>
                {saveMutation.isPending ? tt('termSaving') : tt('termSaveBtn')}
              </Button>
              <Button size="sm" variant="outline" onClick={() => testMutation.mutate()} disabled={testMutation.isPending}>
                {testMutation.isPending ? tt('termTesting') : tt('termTestBtn')}
              </Button>
              {config?.configured && (
                <Button size="sm" variant="outline" className="text-destructive-text" onClick={() => clearMutation.mutate()} disabled={clearMutation.isPending}>
                  {tt('termClearBtn')}
                </Button>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** POS-I218: stored business types that have a label; any other value is shown humanised. */
const BUSINESS_TYPE_KEYS = ['retail', 'restaurant', 'wholesale', 'pharmacy', 'grocery'];

/** POS-I225: an identifier with a Copy button (icon turns into a check for 2 s). */
function CopyValueRow({ label, value, copiedMessage, copyLabel, failedMessage, small }: { label: string; value?: string | null; copiedMessage: string; copyLabel: string; failedMessage: string; small?: boolean }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return undefined;
    const id = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(id);
  }, [copied]);
  const copy = async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      notify.info(copiedMessage);
    } catch {
      notify.warning(failedMessage);
    }
  };
  return (
    <div className="rounded-lg border p-4">
      <p className="text-sm font-medium text-muted-foreground">{label}</p>
      <div className="flex items-start justify-between gap-2">
        <bdi dir="ltr" className={cn('font-mono break-all', small ? 'text-xs text-muted-foreground' : 'text-sm')}>{value || '—'}</bdi>
        {value && (
          <Button type="button" variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={copy} aria-label={`${copyLabel} ${label}`} title={copyLabel}>
            {copied ? <CheckCheck className="h-4 w-4 text-success-text" /> : <Copy className="h-4 w-4" />}
          </Button>
        )}
      </div>
    </div>
  );
}

export function SettingsPage() {
  const { t } = useTranslation('settings');
  const user = useAuthStore((s) => s.user);
  const tenant = useAuthStore((s) => s.tenant);
  const queryClient = useQueryClient();

  const { data: tenantData } = useQuery({
    queryKey: ['tenant-current'],
    queryFn: () => api.get('/tenant').then((r) => r.data),
    staleTime: 30_000,
  });
  const isTestMode: boolean = tenantData?.testMode ?? false;

  const { data: dashboardData } = useQuery({
    queryKey: ['tenant-dashboard'],
    queryFn: () => api.get('/tenant/dashboard').then((r) => r.data),
    staleTime: 60_000,
  });
  const activeAddonKeys: string[] = (dashboardData?.subscription?.addons ?? []).map((a: { key?: string }) => a.key).filter(Boolean);
  const planName: string = dashboardData?.subscription?.planName ?? 'POS Base';

  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canToggle = hasPermission('settings:update');

  // Tax rule settings
  const { data: taxRuleData, isLoading: taxRuleLoading } = useQuery({
    queryKey: ['tenant-tax-rules'],
    queryFn: () => api.get('/tenant/tax-rule-settings').then((r) => r.data),
    staleTime: 30_000,
    enabled: canToggle,
  });

  const [editingPaymentTax, setEditingPaymentTax] = useState(false);
  const [pmRules, setPmRules] = useState<Record<PaymentMethod, PaymentMethodTaxRule> | null>(null);
  const [atlEnabled, setAtlEnabled] = useState<boolean | null>(null);
  const [defaultAtl, setDefaultAtl] = useState<boolean | null>(null);
  const [furtherTaxRate, setFurtherTaxRate] = useState<number | null>(null);
  const [confirmTestMode, setConfirmTestMode] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  // Data management (backup/restore)
  const [dataTab, setDataTab] = useState<'stats' | 'backup' | 'restore'>('stats');
  const [showDataSection, setShowDataSection] = useState(false);
  // In-page view: group the ~26 settings sections into scannable tabs (T-202)
  // Deep link: /settings?tab=team (e.g. trial banner -> plan card) opens that tab.
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab');
  const [settingsTab, setSettingsTab] = useState<'general' | 'tax' | 'team' | 'integrations'>(
    initialTab === 'tax' || initialTab === 'team' || initialTab === 'integrations' ? initialTab : 'general',
  );
  // Scroll to the plan card once it has rendered for a #my-plan deep link.
  useEffect(() => {
    if (window.location.hash !== '#my-plan' || settingsTab !== 'team') return;
    const timer = window.setTimeout(() => {
      document.getElementById('my-plan')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 400);
    return () => window.clearTimeout(timer);
  }, [settingsTab]);
  const [restoreFile, setRestoreFile] = useState<File | null>(null);
  const [restoreResult, setRestoreResult] = useState<{ restored: Record<string, number>; skipped: string[]; recoveryPoint?: { capturedAt: string; totalDeleted: number } } | null>(null);
  const [restoreConfirm, setRestoreConfirm] = useState(false);
  const [restorePreview, setRestorePreview] = useState<{
    tenantName: string;
    backupExportedAt: string | null;
    backupVersion: string;
    canRestore: boolean;
    warnings: string[];
    totalCurrent: number;
    totalIncoming: number;
    lines: { model: string; label: string; currentRows: number; incomingRows: number }[];
  } | null>(null);
  const [preparingRestore, setPreparingRestore] = useState(false);
  const [downloadingBackup, setDownloadingBackup] = useState(false);

  // Profile editing
  const [profileForm, setProfileForm] = useState({ firstName: user?.firstName ?? '', lastName: user?.lastName ?? '' });
  // POS-I213: both names are required (1 to 60 characters once trimmed); Save stays off until valid and changed
  const profileFirst = profileForm.firstName.trim();
  const profileLast = profileForm.lastName.trim();
  const profileFirstError = !profileFirst ? 'firstNameRequired' : profileFirst.length > 60 ? 'firstNameTooLong' : null;
  const profileLastError = !profileLast ? 'lastNameRequired' : profileLast.length > 60 ? 'lastNameTooLong' : null;
  const profileUnchanged = profileFirst === (user?.firstName ?? '') && profileLast === (user?.lastName ?? '');
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  // POS-I214: inline errors per field (no validation toasts), eye toggles, server refusals under their field
  const [passwordTried, setPasswordTried] = useState(false);
  const [passwordShown, setPasswordShown] = useState({ current: false, next: false, confirm: false });
  const [passwordServer, setPasswordServer] = useState<
    { field: 'current' | 'new'; message: string } | { field: 'throttle'; seconds: number } | null
  >(null);
  const [editingProfile, setEditingProfile] = useState(false);
  const [editingPassword, setEditingPassword] = useState(false);
  const [pinForm, setPinForm] = useState({ pin: '', confirm: '' });

  // Business name editing
  const [businessName, setBusinessName] = useState(tenant?.name ?? '');
  const [editingBusiness, setEditingBusiness] = useState(false);
  // POS-I218: seller NTN for the read-only "Details on your receipts" block (same query as Finance > FBR).
  const { data: fbrSettingsData } = useQuery<{ fbrNtn?: string | null }>({
    queryKey: ['fbr-settings'],
    queryFn: async () => api.get('/tenant/fbr-settings').then((r) => r.data),
    enabled: canToggle,
    staleTime: 30_000,
  });

  // Branding
  // POS-I212: the server's one-sentence refusal is shown under the field it names
  const [brandingError, setBrandingError] = useState<string | null>(null);
  const [receiptError, setReceiptError] = useState<string | null>(null);
  const [brandingForm, setBrandingForm] = useState({ brandLogoUrl: tenant?.brandLogoUrl ?? '', brandColor: tenant?.brandColor ?? '', brandTagline: tenant?.brandTagline ?? '' });
  useEffect(() => {
    if (tenant) {
      setBrandingForm({ brandLogoUrl: tenant.brandLogoUrl ?? '', brandColor: tenant.brandColor ?? '', brandTagline: tenant.brandTagline ?? '' });
    }
  // POS-I231: depend on the saved values, not the object, so a refresh or a refused save never wipes what was typed.
  }, [tenant?.brandLogoUrl, tenant?.brandColor, tenant?.brandTagline]); // eslint-disable-line react-hooks/exhaustive-deps
  // POS-I224: follow the saved values from /tenant too (only when they change, never while typing).
  useEffect(() => {
    if (tenantData) {
      setBrandingForm({ brandLogoUrl: tenantData.brandLogoUrl ?? '', brandColor: tenantData.brandColor ?? '', brandTagline: tenantData.brandTagline ?? '' });
    }
  }, [tenantData?.brandLogoUrl, tenantData?.brandColor, tenantData?.brandTagline]); // eslint-disable-line react-hooks/exhaustive-deps

  // File input ref for backup restore
  const restoreFileInputRef = useRef<HTMLInputElement>(null);

  // Keep form state in sync when user/tenant loads
  useEffect(() => {
    if (user && !editingProfile) {
      setProfileForm({ firstName: user.firstName ?? '', lastName: user.lastName ?? '' });
    }
  }, [user, editingProfile]);
  useEffect(() => {
    if (tenant && !editingBusiness) {
      setBusinessName(tenant.name ?? '');
    }
  }, [tenant, editingBusiness]);

  // Receipt customization
  const [receiptFooter, setReceiptFooter] = useState('');
  const [receiptLogoUrl, setReceiptLogoUrl] = useState('');
  // POS-I220: which sections the receipt prints, plus the header note and return policy.
  const [receiptLayout, setReceiptLayout] = useState<ReceiptLayout>(RECEIPT_LAYOUT_DEFAULTS);
  const [receiptSaved, setReceiptSaved] = useState(false);

  // POS-I224: a card is dirty when its form differs from the saved value after the same trimming the
  // server applies, so a saved value never stays "Unsaved". Nothing is dirty before the tenant loads.
  const savedBranding = tenantData ?? tenant;
  const brandingDirty =
    !!savedBranding &&
    (brandingForm.brandLogoUrl.trim() !== (savedBranding.brandLogoUrl ?? '').trim() ||
      brandingForm.brandColor.trim().toLowerCase() !== (savedBranding.brandColor ?? '').trim().toLowerCase() ||
      brandingForm.brandTagline.trim() !== (savedBranding.brandTagline ?? '').trim());
  const receiptDirty =
    !!tenantData &&
    (receiptFooter.trim() !== String(tenantData.settings?.receiptFooter ?? '').trim() ||
      receiptLogoUrl.trim() !== String(tenantData.settings?.receiptLogoUrl ?? '').trim() ||
      receiptLayoutDirty(receiptLayout, resolveReceiptLayout(tenantData.settings?.receiptLayout)));
  const profileDirty = editingProfile && !profileUnchanged;
  const businessDirty = editingBusiness && businessName.trim() !== (tenant?.name ?? '').trim();
  const passwordDirty = editingPassword && !!(passwordForm.currentPassword || passwordForm.newPassword || passwordForm.confirmPassword);
  const leaveGuard = useUnsavedChanges(brandingDirty || receiptDirty || profileDirty || businessDirty || passwordDirty);

  // Initialize receipt fields from tenant settings.
  // POS-I242: depend on the saved receipt values, not the tenant object. Saving the Branding card
  // refetches the tenant (a new object) and the old [tenantData] dependency re-initialised this card,
  // wiping its unsaved edits.
  const savedReceiptLayoutKey = tenantData?.settings
    ? JSON.stringify(resolveReceiptLayout(tenantData.settings.receiptLayout))
    : '';
  useEffect(() => {
    if (tenantData?.settings) {
      setReceiptFooter(tenantData.settings.receiptFooter ?? '');
      setReceiptLogoUrl(tenantData.settings.receiptLogoUrl ?? '');
      setReceiptLayout(resolveReceiptLayout(tenantData.settings.receiptLayout));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!tenantData?.settings, tenantData?.settings?.receiptFooter, tenantData?.settings?.receiptLogoUrl, savedReceiptLayoutKey]);

  const receiptMutation = useMutation({
    mutationFn: (data: { receiptFooter: string; receiptLogoUrl: string; receiptLayout: ReceiptLayout }) =>
      api.patch('/tenant', { settings: data }).then((r) => r.data),
    onSuccess: (data) => {
      setReceiptError(null);
      applyTenantUpdate(queryClient, data);
      setReceiptSaved(true);
      setTimeout(() => setReceiptSaved(false), 2500);
      toast.success(t('receiptSettingsSaved'));
    },
    onError: (err) => {
      const msg = getApiErrorMessage(err, t('failedToSaveReceipt'));
      if (/footer|logo|header note|return policy/i.test(msg)) setReceiptError(msg);
      else toast.error(msg);
    },
  });

  // POS-I224: Manager PIN switches save at once; say so on success and on failure (single-key payload).
  const saveManagerSetting = (settings: Record<string, boolean | number>) => {
    api.patch('/tenant', { settings })
      .then((r) => {
        applyTenantUpdate(queryClient, r.data);
        notify.saved(t('managerPinSettingsEntity'));
      })
      .catch((err) => notify.error(err, t('failedToSaveManagerPin')));
  };

  const profileMutation = useMutation({
    mutationFn: (data: { firstName: string; lastName: string }) =>
      api.patch(`/users/${user?.id}`, data).then((r) => r.data),
    onSuccess: () => {
      toast.success(t('profileUpdated'));
      setEditingProfile(false);
      queryClient.invalidateQueries({ queryKey: ['auth-me'] });
    },
    onError: (err) => notify.error(err, t('failedToUpdateProfile')),
  });

  const passwordMutation = useMutation({
    mutationFn: (data: { currentPassword: string; newPassword: string }) =>
      api.patch('/auth/change-password', data).then((r) => r.data),
    onSuccess: () => {
      toast.success(t('passwordUpdated'));
      setEditingPassword(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordTried(false);
      setPasswordServer(null);
    },
    onError: (err) => {
      const status = getApiErrorStatus(err);
      if (status === 401) {
        setPasswordServer({ field: 'current', message: t('currentPasswordIncorrect') });
      } else if (status === 429 && getApiRetryAfterSeconds(err) !== undefined) {
        setPasswordServer({ field: 'throttle', seconds: getApiRetryAfterSeconds(err) as number });
      } else if (status === 400) {
        setPasswordServer({ field: 'new', message: getApiErrorMessage(err, t('failedToUpdatePassword')) });
      } else {
        notify.error(err, t('failedToUpdatePassword'));
      }
    },
  });

  /** The message key for each box that is wrong, or null (POS-I214). */
  const passwordErrors = (form: { currentPassword: string; newPassword: string; confirmPassword: string }) => {
    const problem = passwordProblem(form.newPassword, user?.email);
    return {
      current: form.currentPassword ? null : ('passwordCurrentRequired' as const),
      next: problem === null ? null : problem === 'common' ? ('passwordNewCommon' as const) : ('passwordNewRules' as const),
      confirm: form.newPassword === form.confirmPassword ? null : ('passwordsMismatch' as const),
    };
  };

  const posPinMutation = useMutation({
    mutationFn: (pin: string) => api.patch('/auth/pos-pin', { pin }).then((r) => r.data),
    onSuccess: () => {
      toast.success(t('posPinUpdated'));
      setPinForm({ pin: '', confirm: '' });
    },
    onError: (err) => notify.error(err, t('failedToSetPin')),
  });

  // Two-Factor Authentication (TOTP)
  const [twoFaSetup, setTwoFaSetup] = useState<{ qrCodeDataUrl: string; secret: string } | null>(null);
  const [twoFaCode, setTwoFaCode] = useState('');
  const [disabling2fa, setDisabling2fa] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [regenerating2fa, setRegenerating2fa] = useState(false);
  const [regenCode, setRegenCode] = useState('');

  const twoFaSetupMutation = useMutation({
    mutationFn: () => api.post('/auth/2fa/setup').then((r) => r.data),
    onSuccess: (data: { qrCodeDataUrl: string; secret: string }) => setTwoFaSetup(data),
    onError: (err) => notify.error(err, 'Failed to start 2FA setup'),
  });

  const twoFaEnableMutation = useMutation({
    mutationFn: (code: string) =>
      api.post('/auth/2fa/enable', { code }).then((r) => r.data as { recoveryCodes?: string[] }),
    onSuccess: (data) => {
      toast.success(t('twoFaEnabledToast'));
      setTwoFaSetup(null);
      setTwoFaCode('');
      setRecoveryCodes(data.recoveryCodes ?? null);
      queryClient.invalidateQueries({ queryKey: ['auth-me'] });
    },
    onError: (err) => notify.error(err, 'Failed to enable 2FA'),
  });

  const twoFaDisableMutation = useMutation({
    mutationFn: (code: string) => api.post('/auth/2fa/disable', { code }).then((r) => r.data),
    onSuccess: () => {
      toast.success(t('twoFaDisabledToast'));
      setDisabling2fa(false);
      setTwoFaCode('');
      setRecoveryCodes(null);
      queryClient.invalidateQueries({ queryKey: ['auth-me'] });
    },
    onError: (err) => notify.error(err, 'Failed to disable 2FA'),
  });

  const twoFaRegenerateMutation = useMutation({
    mutationFn: (code: string) =>
      api.post('/auth/2fa/recovery-codes', { code }).then((r) => r.data as { recoveryCodes: string[] }),
    onSuccess: (data) => {
      toast.success(t('recoveryCodesToast'));
      setRegenerating2fa(false);
      setRegenCode('');
      setRecoveryCodes(data.recoveryCodes);
    },
    onError: (err) => notify.error(err, 'Failed to regenerate recovery codes'),
  });

  const downloadRecoveryCodes = (codes: string[]) => {
    const blob = new Blob(
      [`POS_Light — Two-Factor Recovery Codes\nGenerated: ${new Date().toISOString()}\n\n${codes.join('\n')}\n\nEach code can be used once. Store them somewhere safe.\n`],
      { type: 'text/plain' },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'pos-recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  const businessMutation = useMutation({
    mutationFn: (name: string) => api.patch('/tenant', { name }).then((r) => r.data),
    onSuccess: (data) => {
      toast.success(t('businessNameUpdated'));
      setEditingBusiness(false);
      applyTenantUpdate(queryClient, data);
    },
    onError: (err) => notify.error(err, t('failedToUpdateBusiness')),
  });

  const brandingMutation = useMutation({
    mutationFn: (data: { brandLogoUrl: string | null; brandColor: string | null; brandTagline: string | null }) =>
      api.patch('/tenant', data).then((r) => r.data),
    onSuccess: (data) => {
      setBrandingError(null);
      toast.success(t('brandSaved'));
      applyTenantUpdate(queryClient, data);
    },
    onError: (err) => {
      const msg = getApiErrorMessage(err, t('failedToSaveBranding'));
      if (/logo|colou?r|tagline/i.test(msg)) setBrandingError(msg);
      else notify.error(err, t('failedToSaveBranding'));
    },
  });

  const taxRuleMutation = useMutation({
    mutationFn: (data: TaxRuleSettingsPatch) => api.patch('/tenant/tax-rule-settings', data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-tax-rules'] });
      queryClient.invalidateQueries({ queryKey: ['pos-products'] });
      toast.success(t('taxSettingsSaved'));
      setEditingPaymentTax(false);
    },
    onError: (err: unknown) => toast.error(getApiErrorMessage(err, t('failedToSaveTax'))),
  });

  const toggleMutation = useMutation({
    mutationFn: (enabled: boolean) =>
      api.patch('/tenant/test-mode', { enabled }).then((r) => r.data),
    onSuccess: (data, enabled) => {
      applyTenantUpdate(queryClient, data);
      // Invalidate all catalog queries — data is now filtered by the new mode
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      queryClient.invalidateQueries({ queryKey: ['pos-products'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-products'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-movements'] });
      queryClient.invalidateQueries({ queryKey: ['purchases'] });
      toast.success(enabled ? t('switchModeSuccessTest') : t('switchModeSuccessProduction'));
    },
    onError: () => toast.error(t('failedToSwitchMode')),
  });

  const resetTestDataMutation = useMutation({
    mutationFn: () => api.post('/tenant/reset-test-data').then((r) => r.data),
    onSuccess: (data) => {
      const total = Object.values(data.deleted as Record<string, number>).reduce((s, n) => s + n, 0);
      toast.success(t('testDataCleared', { total, stockReset: data.stockReset }));
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-products'] });
      queryClient.invalidateQueries({ queryKey: ['sales'] });
      queryClient.invalidateQueries({ queryKey: ['purchases'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-movements'] });
      queryClient.invalidateQueries({ queryKey: ['tenant-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['journal-entries'] });
      queryClient.invalidateQueries({ queryKey: ['dining-orders'] });
    },
    onError: (err) => notify.error(err, t('failedToResetTestData')),
  });

  // ── Data stats (lazy — only fetches when section expanded + stats tab open)
  const { data: dbStats = [], isLoading: statsLoading } = useQuery<DataStatRow[]>({
    queryKey: ['tenant-data-stats'],
    queryFn: () => api.get('/tenant/data/stats').then((r) => r.data),
    enabled: showDataSection && dataTab === 'stats' && canToggle,
    staleTime: 60_000,
  });

  const restoreMutation = useMutation({
    mutationFn: (backupData: Record<string, unknown>) => api.post('/tenant/data/restore', backupData).then((r) => r.data),
    onSuccess: (data) => {
      setRestoreResult(data);
      setRestoreFile(null);
      toast.success(t('restoreDoneToast'));
    },
    onError: (e) => notify.error(e, 'Restore failed'),
  });

  const handleDownloadBackup = async () => {
    setDownloadingBackup(true);
    try {
      const response = await api.get('/tenant/data/backup', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      const today = new Date().toISOString().split('T')[0];
      a.setAttribute('download', `pos-backup-${tenant?.id?.slice(0, 8) ?? 'tenant'}-${today}.json`);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t('backupDownloadedToast'));
    } catch {
      toast.error(t('backupFailedToast'));
    } finally {
      setDownloadingBackup(false);
    }
  };

  const prepareRestore = async (file: File) => {
    setPreparingRestore(true);
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const preview = await api.post('/tenant/data/restore/preview', data).then((r) => r.data);
      if (!preview.canRestore) {
        toast.error(preview.warnings?.[0] ?? 'This backup cannot be restored to the current tenant.');
        setPreparingRestore(false);
        return;
      }
      setRestorePreview(preview);
      setRestoreConfirm(true);
    } catch (e: unknown) {
      toast.error(getApiErrorMessage(e, 'Invalid backup file — could not parse or preview JSON'));
    } finally {
      setPreparingRestore(false);
    }
  };

  const handleRestoreFile = async (file: File) => {
    setRestoreConfirm(false);
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      // Recovery point: auto-download a full safety backup of the CURRENT data before
      // it is wiped, so the restore is reversible.
      await handleDownloadBackup();
      restoreMutation.mutate(data);
    } catch {
      toast.error(t('backupInvalidToast'));
    }
  };
  const currentPmRules = pmRules ?? (taxRuleData?.paymentMethodTaxRules as Record<PaymentMethod, PaymentMethodTaxRule> | null);
  const currentAtlEnabled = atlEnabled ?? (taxRuleData?.atlEnabled as boolean ?? false);
  const currentDefaultAtl = defaultAtl ?? (taxRuleData?.defaultBuyerAtlStatus as boolean ?? false);
  const currentFurtherTaxRate = furtherTaxRate ?? (taxRuleData?.furtherTaxRate as number ?? 3);
  const paymentMethodEnabled = !!currentPmRules && Object.keys(currentPmRules).length > 0;

  const initPaymentRules = () => {
    const defaults: Record<PaymentMethod, PaymentMethodTaxRule> = {
      CASH:          { rate: 15, name: 'Sales Tax (Cash)',    overridesProductTax: true },
      CARD:          { rate: 5,  name: 'Sales Tax (Digital)', overridesProductTax: true },
      MOBILE_WALLET: { rate: 5,  name: 'Sales Tax (Digital)', overridesProductTax: true },
      BANK_TRANSFER: { rate: 5,  name: 'Sales Tax (Digital)', overridesProductTax: true },
      CREDIT:        { rate: 5,  name: 'Sales Tax (Digital)', overridesProductTax: true },
    };
    const existing = taxRuleData?.paymentMethodTaxRules ?? {};
    const merged = { ...defaults, ...existing } as Record<PaymentMethod, PaymentMethodTaxRule>;
    setPmRules(merged);
    setEditingPaymentTax(true);
  };

  // SMS Notifications
  const smsReceiptsEnabled: boolean = tenantData?.smsReceiptsEnabled ?? false;

  // Communications settings
  const { data: commData, isLoading: commLoading } = useQuery({
    queryKey: ['comm-settings'],
    queryFn: () => api.get('/tenant/communication-settings').then((r) => r.data),
    staleTime: 30_000,
    enabled: canToggle,
  });
  const [emailForm, setEmailForm] = useState<Partial<EmailCommForm>>({});
  const [smsCommForm, setSmsCommForm] = useState<Partial<SmsCommForm>>({});
  const [waForm, setWaForm] = useState<Partial<WhatsappCommForm>>({});
  const [copiedPortal, setCopiedPortal] = useState(false);
  const commMutation = useMutation({
    mutationFn: (body: CommSettingsPatch) => api.patch('/tenant/communication-settings', body).then((r) => r.data),
    onSuccess: () => {
      toast.success(t('commSettingsSaved'));
      queryClient.invalidateQueries({ queryKey: ['comm-settings'] });
    },
    onError: (err: unknown) => toast.error(getApiErrorMessage(err, t('failedToSaveComm'))),
  });

  const smsMutation = useMutation({mutationFn: (enabled: boolean) => api.patch('/tenant', { smsReceiptsEnabled: enabled }).then((r) => r.data),
    onSuccess: (data) => {
      applyTenantUpdate(queryClient, data);
      toast.success(t('smsSettingsSaved'));
    },
    onError: () => toast.error(t('failedToSaveSms')),
  });
  const [testSmsPhone, setTestSmsPhone] = useState('');
  const [sendingSms, setSendingSms] = useState(false);
  const [smsTestOpen, setSmsTestOpen] = useState(false);
  const sendTestSms = async () => {
    if (!testSmsPhone.trim()) { toast.error(t('enterPhoneError')); return; }
    setSendingSms(true);
    try {
      const res = await api.post('/tenant/send-test-sms', { phone: testSmsPhone.trim() });
      if (res.data?.sent === false) {
        // POS-I285: nothing was sent (no provider, or the provider refused): say so and keep the dialog open
        toast.error(typeof res.data?.message === 'string' ? res.data.message : t('failedToSendTestSms'));
      } else {
        toast.success(t('testSmsSent'));
        setSmsTestOpen(false);
        setTestSmsPhone('');
      }
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, t('failedToSendTestSms')));
    } finally {
      setSendingSms(false);
    }
  };

  // PO Approval
  const requirePoApproval: boolean = tenantData?.requirePoApproval ?? false;
  const poApprovalMutation = useMutation({
    mutationFn: (enabled: boolean) => api.patch('/tenant', { requirePoApproval: enabled }).then((r) => r.data),
    onSuccess: (data) => {
      applyTenantUpdate(queryClient, data);
      toast.success(t('poApprovalSaved'));
    },
    onError: () => toast.error(t('failedToSavePo')),
  });

  const approvalThresholdMutation = useMutation({
    mutationFn: (payload: { expenseApprovalThreshold?: number; vendorPaymentApprovalThreshold?: number; refundApprovalThreshold?: number; tipApprovalThreshold?: number; cashVarianceTolerance?: number }) =>
      api.patch('/tenant', payload).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-current'] });
      toast.success(t('approvalThresholdSaved'));
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message;
      toast.error((Array.isArray(msg) ? msg[0] : typeof msg === 'string' ? msg : undefined) ?? t('failedToSaveThreshold'));
    },
  });

  // POS-I92: blind cash count at shift close.
  const blindCountMutation = useMutation({
    mutationFn: (enabled: boolean) => api.patch('/tenant', { blindCashCount: enabled }).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-current'] });
      queryClient.invalidateQueries({ queryKey: ['shift-current'] });
      toast.success(t('approvalThresholdSaved'));
    },
    onError: () => toast.error(t('failedToSaveThreshold')),
  });

  // Dining service-charge destination head (T-380)
  const serviceChargeIsStaffPool: boolean = tenantData?.serviceChargeIsStaffPool ?? false;
  const serviceChargePoolMutation = useMutation({
    mutationFn: (enabled: boolean) => api.patch('/tenant', { serviceChargeIsStaffPool: enabled }).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-current'] });
      toast.success(t('serviceChargePoolSaved'));
    },
    onError: () => toast.error(t('failedToSaveServiceChargePool')),
  });

  // Inventory Costing (WAC/FIFO) — T-203, advanced_costing addon
  const canCostingSettings = hasPermission('inventory-costing:manage');
  // POS-I232: decided by the permission (the backend gate), not by an add-on row: a trial holds
  // inventory-costing:manage through its derived access and has no advanced_costing row.
  const costingAddonActive = canCostingSettings;
  const { data: costingSettings, isLoading: costingSettingsLoading } = useQuery<{ costMethod: 'WAC' | 'FIFO'; onHandValue: number }>({
    queryKey: ['inventory-costing-settings'],
    queryFn: () => api.get('/inventory/costing-settings').then((r) => r.data),
    enabled: costingAddonActive && canCostingSettings,
    staleTime: 30_000,
  });
  const [costingSwitchTarget, setCostingSwitchTarget] = useState<'WAC' | 'FIFO' | null>(null);
  const costingSwitchMutation = useMutation({
    mutationFn: (costMethod: 'WAC' | 'FIFO') =>
      api.patch('/inventory/costing-settings', { costMethod }).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory-costing-settings'] });
      toast.success(t('costingSwitchSaved'));
      setCostingSwitchTarget(null);
    },
    onError: () => toast.error(t('costingSwitchFailed')),
  });

  // Scheduled Reports
  const { data: reportSchedules, isLoading: reportSchedulesLoading } = useQuery<ReportSchedule[]>({
    queryKey: ['report-schedules'],
    queryFn: () => api.get('/tenant/report-schedules').then((r) => r.data),
    staleTime: 30_000,
    enabled: canToggle,
  });
  const [localSchedules, setLocalSchedules] = useState<ReportSchedule[]>([]);
  useEffect(() => {
    if (reportSchedules) setLocalSchedules(reportSchedules);
  }, [reportSchedules]);
  const reportScheduleMutation = useMutation({
    mutationFn: (schedules: ReportSchedule[]) => api.patch('/tenant/report-schedules', { schedules }).then((r) => r.data),
    onSuccess: (data) => {
      toast.success(t('schedSavedToast'));
      queryClient.invalidateQueries({ queryKey: ['report-schedules'] });
      setLocalSchedules(data);
    },
    onError: (err: unknown) => toast.error(getApiErrorMessage(err, 'Failed to save report schedules')),
  });
  const updateLocalSchedule = (id: string, patch: Partial<ReportSchedule>) => {
    setLocalSchedules((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  };

  // API Keys
  const [apiKeyForm, setApiKeyForm] = useState({ name: '', description: '' });
  const [newApiKey, setNewApiKey] = useState<string | null>(null);
  const [addApiKeyOpen, setAddApiKeyOpen] = useState(false);
  const [revokeApiKeyId, setRevokeApiKeyId] = useState<string | null>(null);
  const canManageApiKeys = hasPermission('api:manage') || hasPermission('api:*');
  const { data: apiKeysData, isLoading: apiKeysLoading } = useQuery({
    queryKey: ['api-keys'],
    queryFn: () => api.get('/api-keys').then((r) => r.data),
    enabled: hasPermission('api:read') || hasPermission('api:*'),
    staleTime: 30_000,
  });
  const apiKeys: ApiKeyRow[] = apiKeysData?.data ?? [];
  const createApiKeyMutation = useMutation({
    mutationFn: (d: { name: string; description?: string }) => api.post('/api-keys', d).then((r) => r.data),
    onSuccess: (res: { key: string }) => {
      setNewApiKey(res.key);
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      setApiKeyForm({ name: '', description: '' });
    },
    onError: (err) => notify.error(err, t('failedToCreateApiKey')),
  });
  const revokeApiKeyMutation = useMutation({
    mutationFn: (id: string) => api.post(`/api-keys/${id}/revoke`).then((r) => r.data),
    onSuccess: () => {
      toast.success(t('apiKeyRevoked'));
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      setRevokeApiKeyId(null);
    },
    onError: (err) => notify.error(err, t('failedToRevoke')),
  });

  // Webhooks
  const WEBHOOK_EVENTS = ['sale.created', 'sale.voided', 'sale.refunded', 'purchase.received', 'low_stock.alert', 'customer.created', 'stock_transfer.completed'];
  const [webhookForm, setWebhookForm] = useState({ name: '', url: '', events: [] as string[] });
  const [addWebhookOpen, setAddWebhookOpen] = useState(false);
  const [deleteWebhookId, setDeleteWebhookId] = useState<string | null>(null);
  const [newWebhookSecret, setNewWebhookSecret] = useState<string | null>(null);
  const canManageWebhooks = hasPermission('api:write') || hasPermission('api:*');
  const { data: webhooksData, isLoading: webhooksLoading } = useQuery({
    queryKey: ['webhooks'],
    queryFn: () => api.get('/webhooks').then((r) => r.data),
    enabled: hasPermission('api:read') || hasPermission('api:*'),
    staleTime: 30_000,
  });
  const webhooks: WebhookRow[] = Array.isArray(webhooksData) ? webhooksData : [];
  const createWebhookMutation = useMutation({
    mutationFn: (d: { name: string; url: string; events: string[] }) => api.post('/webhooks', d).then((r) => r.data),
    onSuccess: (res: { secret?: string }) => {
      toast.success(t('whCreatedToast'));
      setNewWebhookSecret(res.secret ?? null);
      queryClient.invalidateQueries({ queryKey: ['webhooks'] });
      setWebhookForm({ name: '', url: '', events: [] });
      setAddWebhookOpen(false);
    },
    onError: (err) => notify.error(err, 'Failed to create webhook'),
  });
  const deleteWebhookMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/webhooks/${id}`),
    onSuccess: () => {
      toast.success(t('whDeletedToast'));
      queryClient.invalidateQueries({ queryKey: ['webhooks'] });
      setDeleteWebhookId(null);
    },
    onError: (err) => notify.error(err, 'Failed to delete webhook'),
  });
  const toggleWebhookEventFilter = (evt: string) => {
    setWebhookForm((prev) => ({
      ...prev,
      events: prev.events.includes(evt) ? prev.events.filter((e) => e !== evt) : [...prev.events, evt],
    }));
  };

  // Receipt Printer (ESC/POS via WebUSB)
  const [printerConnected, setPrinterConnected] = useState(false);
  const [printerDeviceName, setPrinterDeviceName] = useState<string | null>(null);
  const [printerConnecting, setPrinterConnecting] = useState(false);
  const [printerTesting, setPrinterTesting] = useState(false);
  const [printerSettings, setPrinterSettings] = useState(() => {
    try {
      const raw = localStorage.getItem('pos-printer-settings');
      if (raw) return { paperWidth: 80 as 58 | 80, cutPaper: true, openDrawer: true, ...JSON.parse(raw) };
    } catch {}
    return { paperWidth: 80 as 58 | 80, cutPaper: true, openDrawer: true };
  });
  const printerSupported = typeof navigator !== 'undefined' && 'usb' in navigator;

  const handlePrinterConnect = async () => {
    const { printerService } = await import('@/lib/printer');
    setPrinterConnecting(true);
    try {
      await printerService.connect();
      setPrinterConnected(true);
      setPrinterDeviceName(printerService.getDeviceName());
      toast.success(`Printer connected: ${printerService.getDeviceName()}`);
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to connect printer');
    } finally {
      setPrinterConnecting(false);
    }
  };

  const handlePrinterDisconnect = async () => {
    const { printerService } = await import('@/lib/printer');
    await printerService.disconnect();
    setPrinterConnected(false);
    setPrinterDeviceName(null);
    toast.success(t('prnDisconnectedToast'));
  };

  const handlePrinterTest = async () => {
    const { printerService } = await import('@/lib/printer');
    if (!printerService.isConnected()) {
      toast.error(t('prnNotConnectedToast'));
      return;
    }
    setPrinterTesting(true);
    try {
      await printerService.testPrint();
      toast.success(t('prnTestPrintedToast'));
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Print failed');
    } finally {
      setPrinterTesting(false);
    }
  };

  const savePrinterSettings = (patch: Partial<typeof printerSettings>) => {
    const updated = { ...printerSettings, ...patch };
    setPrinterSettings(updated);
    localStorage.setItem('pos-printer-settings', JSON.stringify({
      ...updated,
      charsPerLine: updated.paperWidth === 58 ? 32 : 48,
    }));
    import('@/lib/printer').then(({ printerService }) => printerService.saveSettings({
      ...updated,
      charsPerLine: updated.paperWidth === 58 ? 32 : 48,
    }));
    // POS-I221: these live in this browser only, so each change says so.
    notify.info(t('printerSettingSaved'));
  };

  return (
    <>
    <div className="space-y-6 page-enter">
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        actions={
          <Link to="/store-guide">
            <Button variant="outline" className="flex items-center gap-2">
              <BookOpen className="h-4 w-4" />
              {t('storeGuide')}
            </Button>
          </Link>
        }
      />

      <TabBar
        tabs={[
          { key: 'general', label: t('tabGeneral') },
          { key: 'tax', label: t('tabTax') },
          { key: 'team', label: t('tabTeam') },
          { key: 'integrations', label: t('tabIntegrations') },
        ]}
        activeTab={settingsTab}
        onTabChange={(k) => setSettingsTab(k as 'general' | 'tax' | 'team' | 'integrations')}
        bordered={false}
      />

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        {settingsTab === 'general' && (<>
        {/* Profile */}
        <Card className="animate-fade-in-up stagger-1 hover:shadow-glow-sm transition-all duration-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <User className="h-5 w-5" />
              {t('profileTitle')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-primary via-primary to-primary text-xl font-bold text-primary-foreground">
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </div>
              <div>
                <p className="text-lg font-semibold">{user?.firstName} {user?.lastName}</p>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
                <div className="mt-1 flex gap-1">
                  {user?.roles?.map((r: string) => (
                    <span key={r} className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary-text">
                      {r}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Edit Name */}
            {!editingProfile ? (
              <Button variant="outline" size="sm" onClick={() => setEditingProfile(true)}>
                {t('editName')}
              </Button>
            ) : (
              <div className="space-y-3 rounded-lg border p-3">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs" htmlFor="profileFirstName">{t('firstName')} <span className="text-destructive-text" aria-hidden="true">*</span></Label>
                    <Input
                      id="profileFirstName"
                      value={profileForm.firstName}
                      onChange={(e) => setProfileForm({ ...profileForm, firstName: e.target.value })}
                      placeholder={t('firstNamePlaceholder')}
                      maxLength={60}
                      required
                      aria-invalid={!!profileFirstError}
                    />
                    {profileFirstError && <p role="alert" className="mt-1 text-xs text-destructive-text text-start">{t(profileFirstError)}</p>}
                  </div>
                  <div>
                    <Label className="text-xs" htmlFor="profileLastName">{t('lastName')} <span className="text-destructive-text" aria-hidden="true">*</span></Label>
                    <Input
                      id="profileLastName"
                      value={profileForm.lastName}
                      onChange={(e) => setProfileForm({ ...profileForm, lastName: e.target.value })}
                      placeholder={t('lastNamePlaceholder')}
                      maxLength={60}
                      required
                      aria-invalid={!!profileLastError}
                    />
                    {profileLastError && <p role="alert" className="mt-1 text-xs text-destructive-text text-start">{t(profileLastError)}</p>}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    disabled={profileMutation.isPending || !!profileFirstError || !!profileLastError || profileUnchanged}
                    onClick={() => profileMutation.mutate({ firstName: profileFirst, lastName: profileLast })}
                  >
                    {t('save')}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => { setEditingProfile(false); setProfileForm({ firstName: user?.firstName ?? '', lastName: user?.lastName ?? '' }); }}>
                    {t('cancel')}
                  </Button>
                </div>
              </div>
            )}

            {/* Change Password */}
            {!editingPassword ? (
              <Button variant="outline" size="sm" onClick={() => setEditingPassword(true)}>
                {t('changePassword')}
              </Button>
            ) : (
              <div className="space-y-3 rounded-lg border p-3">
                {([
                  {
                    key: 'current' as const,
                    id: 'currentPassword',
                    field: 'currentPassword' as const,
                    label: t('currentPassword'),
                    placeholder: t('passwordCurrentPlaceholder'),
                    autoComplete: 'current-password',
                    error: passwordServer?.field === 'current'
                      ? passwordServer.message
                      : passwordTried && passwordErrors(passwordForm).current ? t('passwordCurrentRequired') : null,
                  },
                  {
                    key: 'next' as const,
                    id: 'newPassword',
                    field: 'newPassword' as const,
                    label: t('newPassword'),
                    placeholder: t('passwordNewPlaceholder'),
                    autoComplete: 'new-password',
                    error: passwordServer?.field === 'new'
                      ? passwordServer.message
                      : passwordTried && passwordErrors(passwordForm).next ? t(passwordErrors(passwordForm).next as 'passwordNewRules') : null,
                  },
                  {
                    key: 'confirm' as const,
                    id: 'confirmNewPassword',
                    field: 'confirmPassword' as const,
                    label: t('confirmPassword'),
                    placeholder: t('passwordRepeatPlaceholder'),
                    autoComplete: 'new-password',
                    error: passwordTried && passwordErrors(passwordForm).confirm ? t('passwordsMismatch') : null,
                  },
                ]).map(({ key, id, field, label, placeholder, autoComplete, error }) => (
                  <div key={id}>
                    <Label className="text-xs" htmlFor={id}>{label}</Label>
                    <div className="relative">
                      <Input
                        id={id}
                        type={passwordShown[key] ? 'text' : 'password'}
                        value={passwordForm[field]}
                        onChange={(e) => {
                          setPasswordForm({ ...passwordForm, [field]: e.target.value });
                          setPasswordServer(null);
                        }}
                        placeholder={placeholder}
                        autoComplete={autoComplete}
                        maxLength={72}
                        className="pe-10"
                        aria-invalid={!!error}
                      />
                      <button
                        type="button"
                        className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        aria-label={passwordShown[key] ? t('hidePassword') : t('showPassword')}
                        onClick={() => setPasswordShown({ ...passwordShown, [key]: !passwordShown[key] })}
                      >
                        {passwordShown[key] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {error && <p role="alert" className="mt-1 text-xs text-destructive-text text-start">{error}</p>}
                  </div>
                ))}
                {passwordServer?.field === 'throttle' && <ThrottleNotice seconds={passwordServer.seconds} />}
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    disabled={passwordMutation.isPending}
                    onClick={() => {
                      setPasswordTried(true);
                      setPasswordServer(null);
                      const errs = passwordErrors(passwordForm);
                      if (errs.current || errs.next || errs.confirm) return;
                      passwordMutation.mutate({
                        currentPassword: passwordForm.currentPassword,
                        newPassword: passwordForm.newPassword,
                      });
                    }}
                  >
                    {passwordMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : t('updatePassword')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setEditingPassword(false);
                      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                      setPasswordTried(false);
                      setPasswordServer(null);
                      setPasswordShown({ current: false, next: false, confirm: false });
                    }}
                  >
                    {t('cancel')}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* POS PIN */}
        <Card className="animate-fade-in-up stagger-2 hover:shadow-glow-sm transition-all duration-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Key className="h-5 w-5" />
              {t('posPinTitle')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="rounded-lg border p-4 space-y-3">
              <p className="text-sm text-muted-foreground">
                {t('posPinDesc')}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">{t('posPinNew')}</Label>
                  <Input
                    type="password"
                    inputMode="numeric"
                    maxLength={4}
                    value={pinForm.pin}
                    onChange={(e) => setPinForm({ ...pinForm, pin: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                    placeholder="••••"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">{t('posPinConfirm')}</Label>
                  <Input
                    type="password"
                    inputMode="numeric"
                    maxLength={4}
                    value={pinForm.confirm}
                    onChange={(e) => setPinForm({ ...pinForm, confirm: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                    placeholder="••••"
                  />
                </div>
              </div>
              <Button
                size="sm"
                disabled={posPinMutation.isPending || pinForm.pin.length !== 4 || pinForm.confirm.length !== 4}
                onClick={() => {
                  if (pinForm.pin !== pinForm.confirm) { toast.error(t('posPinMismatch')); return; }
                  posPinMutation.mutate(pinForm.pin);
                }}
              >
                {posPinMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : t('posPinSet')}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Two-Factor Authentication */}
        <Card className="animate-fade-in-up stagger-2 hover:shadow-glow-sm transition-all duration-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <ShieldCheck className="h-5 w-5" />
              {t('twoFaTitle')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="rounded-lg border p-4 space-y-3">
              {recoveryCodes && (
                <div className="rounded-md border border-warning/30 bg-warning/10 p-3 space-y-2">
                  <div className="flex items-center gap-2 text-sm font-medium text-warning-text">
                    <Key className="h-4 w-4" />
                    {t('twoFaSaveCodes')}
                  </div>
                  <p className="text-xs text-warning-text">
                    {t('twoFaCodesNote')}
                  </p>
                  <div className="grid grid-cols-2 gap-1 rounded bg-background/60 p-2 font-mono text-sm">
                    {recoveryCodes.map((c) => (
                      <span key={c} className="tracking-wider">{c}</span>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { navigator.clipboard.writeText(recoveryCodes.join('\n')); toast.success(t('twoFaCodesCopied')); }}
                    >
                      <Copy className="h-4 w-4 me-1" /> {t('twoFaCopyAll')}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => downloadRecoveryCodes(recoveryCodes)}>
                      <Download className="h-4 w-4 me-1" /> {t('twoFaDownload')}
                    </Button>
                    <Button size="sm" onClick={() => setRecoveryCodes(null)}>
                      <CheckCheck className="h-4 w-4 me-1" /> {t('twoFaSaved')}
                    </Button>
                  </div>
                </div>
              )}
              {user?.twoFactorEnabled ? (
                <>
                  <div className="flex items-center gap-2 text-sm text-success-text">
                    <CheckCircle2 className="h-4 w-4" />
                    {t('twoFaEnabledNote')}
                  </div>
                  {!disabling2fa && !regenerating2fa && (
                    <div className="flex flex-wrap gap-2">
                      <Button variant="outline" size="sm" onClick={() => setRegenerating2fa(true)}>
                        <Key className="h-4 w-4 me-1" /> {t('twoFaRegenerate')}
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setDisabling2fa(true)}>
                        {t('twoFaDisable')}
                      </Button>
                    </div>
                  )}
                  {regenerating2fa && (
                    <div className="space-y-2">
                      <p className="text-xs text-muted-foreground">{t('twoFaRegenerateHelp')}</p>
                      <Input
                        inputMode="numeric"
                        maxLength={6}
                        value={regenCode}
                        onChange={(e) => setRegenCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="123456"
                        className="w-40 text-center tracking-[0.3em]"
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          disabled={twoFaRegenerateMutation.isPending || regenCode.length !== 6}
                          onClick={() => twoFaRegenerateMutation.mutate(regenCode)}
                        >
                          {twoFaRegenerateMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : t('twoFaGenerate')}
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => { setRegenerating2fa(false); setRegenCode(''); }}>
                          {t('cancel')}
                        </Button>
                      </div>
                    </div>
                  )}
                  {disabling2fa && (
                    <div className="space-y-2">
                      <p className="text-xs text-muted-foreground">{t('twoFaDisableHelp')}</p>
                      <Input
                        inputMode="numeric"
                        maxLength={6}
                        value={twoFaCode}
                        onChange={(e) => setTwoFaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="123456"
                        className="w-40 text-center tracking-[0.3em]"
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="destructive"
                          disabled={twoFaDisableMutation.isPending || twoFaCode.length !== 6}
                          onClick={() => twoFaDisableMutation.mutate(twoFaCode)}
                        >
                          {twoFaDisableMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : t('twoFaConfirmDisable')}
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => { setDisabling2fa(false); setTwoFaCode(''); }}>
                          {t('cancel')}
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              ) : !twoFaSetup ? (
                <>
                  <p className="text-sm text-muted-foreground">
                    {t('twoFaIntro')}
                  </p>
                  <Button
                    size="sm"
                    disabled={twoFaSetupMutation.isPending}
                    onClick={() => twoFaSetupMutation.mutate()}
                  >
                    {twoFaSetupMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : t('twoFaEnable')}
                  </Button>
                </>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    {t('twoFaScan')}
                  </p>
                  <img src={twoFaSetup.qrCodeDataUrl} alt="2FA QR code" className="h-44 w-44 rounded-lg border bg-card p-2" />
                  <p className="text-xs text-muted-foreground">
                    {t('twoFaManualKey')} <code className="rounded bg-muted px-1 py-0.5 font-mono">{twoFaSetup.secret}</code>
                  </p>
                  <Input
                    inputMode="numeric"
                    maxLength={6}
                    value={twoFaCode}
                    onChange={(e) => setTwoFaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="123456"
                    className="w-40 text-center tracking-[0.3em]"
                  />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      disabled={twoFaEnableMutation.isPending || twoFaCode.length !== 6}
                      onClick={() => twoFaEnableMutation.mutate(twoFaCode)}
                    >
                      {twoFaEnableMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : t('twoFaVerifyEnable')}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => { setTwoFaSetup(null); setTwoFaCode(''); }}>
                      {t('cancel')}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Business */}
        <Card className="animate-fade-in-up stagger-2 hover:shadow-glow-sm transition-all duration-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Building2 className="h-5 w-5" />
              {t('businessTitle')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="rounded-lg border p-4">
              <p className="text-sm font-medium text-muted-foreground">{t('businessNameLabel')}</p>
              {!editingBusiness ? (
                <div className="flex items-center justify-between">
                  <p className="text-lg font-semibold">{tenant?.name || '—'}</p>
                  {canToggle && (
                    <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setEditingBusiness(true)}>
                      {t('edit')}
                    </Button>
                  )}
                </div>
              ) : (
                <div className="mt-2 space-y-2">
                  <Input
                    value={businessName}
                    onChange={(e) => setBusinessName(e.target.value)}
                    placeholder={t('businessNamePlaceholder')}
                  />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      disabled={businessMutation.isPending || !businessName.trim()}
                      onClick={() => businessMutation.mutate(businessName.trim())}
                    >
                      {t('save')}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => { setEditingBusiness(false); setBusinessName(tenant?.name ?? ''); }}>
                      {t('cancel')}
                    </Button>
                  </div>
                </div>
              )}
            </div>
            <CopyValueRow label={t('businessIdLabel')} value={tenant?.slug} copiedMessage={t('businessIdCopied')} copyLabel={t('copy')} failedMessage={t('copyFailed')} />
            <CopyValueRow label={t('tenantIdLabel')} value={tenant?.id} copiedMessage={t('tenantIdCopied')} copyLabel={t('copy')} failedMessage={t('copyFailed')} small />
            <p className="text-xs text-muted-foreground">{t('idsSupportNote')}</p>
            <div className="rounded-lg border p-4 space-y-1.5 text-sm">
              <p className="font-medium text-muted-foreground">{t('receiptDetailsTitle')}</p>
              <p>{t('receiptCurrency')}: <bdi dir="ltr">{tenantData?.settings?.currency || '—'}</bdi></p>
              <p>{t('receiptTimezone')}: <bdi dir="ltr">{tenantData?.settings?.timezone || '—'}</bdi></p>
              <p>
                {t('receiptBusinessType')}:{' '}
                {tenantData?.settings?.businessType
                  ? (BUSINESS_TYPE_KEYS.includes(tenantData.settings.businessType)
                      ? t(`businessType_${tenantData.settings.businessType}`)
                      : humanizeEnum(tenantData.settings.businessType))
                  : '—'}
              </p>
              <p>
                {t('receiptAddressPhone')}{' '}
                <Link to="/inventory/locations" className="text-primary underline-offset-2 hover:underline">{t('receiptAddressPhoneLink')}</Link>
              </p>
              <p>
                {t('receiptSellerNtn')}:{' '}
                {formatSellerNtn(fbrSettingsData?.fbrNtn) ? (
                  <bdi dir="ltr">{formatSellerNtn(fbrSettingsData?.fbrNtn)}</bdi>
                ) : (
                  <>
                    {t('receiptNtnNotSet')}{' '}
                    {canToggle && <Link to="/finance/fbr" className="text-primary underline-offset-2 hover:underline">{t('receiptNtnSetLink')}</Link>}
                  </>
                )}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Branding */}
        {canToggle && (
          <Card className="animate-fade-in-up stagger-2 hover:shadow-glow-sm transition-all duration-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Palette className="h-5 w-5" />
                {t('brandingTitle')}
              </CardTitle>
              <p className="text-sm text-muted-foreground">{t('brandingIntro')}</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="brandLogoUrl">{t('logoUrl')}</Label>
                  <LogoUploadField
                    id="brandLogoUrl"
                    value={brandingForm.brandLogoUrl}
                    savedValue={savedBranding.brandLogoUrl ?? ''}
                    onChange={(v) => setBrandingForm((f) => ({ ...f, brandLogoUrl: v }))}
                    onClearError={() => setBrandingError(null)}
                    error={brandingError && /logo/i.test(brandingError) ? brandingError : null}
                    placeholder="https://cdn.example.com/logo.png"
                  />
                  <p className="text-xs text-muted-foreground">{t('brandLogoHelp')}</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="brandColor">{t('brandColorLabel')}</Label>
                  <div className="flex items-center gap-2">
                    <label
                      className="relative h-9 w-9 shrink-0 cursor-pointer overflow-hidden rounded-lg border focus-within:ring-2 focus-within:ring-ring"
                      style={{ background: isHexColor(brandingForm.brandColor) ? brandingForm.brandColor : undefined }}
                      title={t('brandColorPick')}
                    >
                      <span className={isHexColor(brandingForm.brandColor) ? 'sr-only' : 'block h-full w-full bg-gradient-to-br from-primary to-primary/70'} />
                      <input
                        id="brandColor"
                        type="color"
                        aria-label={t('brandColorPick')}
                        value={isHexColor(brandingForm.brandColor) ? brandingForm.brandColor : '#6366f1'}
                        onChange={(e) => { setBrandingError(null); setBrandingForm((f) => ({ ...f, brandColor: e.target.value })); }}
                        className="sr-only"
                      />
                    </label>
                    <Input
                      value={brandingForm.brandColor}
                      onChange={(e) => { setBrandingError(null); setBrandingForm((f) => ({ ...f, brandColor: e.target.value })); }}
                      placeholder="#6366f1"
                      aria-label={t('brandColorHex')}
                      dir="ltr"
                      className="font-mono w-28"
                      aria-invalid={!!brandingError && /colou?r/i.test(brandingError)}
                    />
                    {brandingForm.brandColor && (
                      <button type="button" onClick={() => setBrandingForm((f) => ({ ...f, brandColor: '' }))} className="text-xs text-muted-foreground hover:text-foreground">
                        {t('brandColorReset')}
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2" role="group" aria-label={t('brandPresets')}>
                    {BRAND_PRESETS.map((hex) => (
                      <button
                        key={hex}
                        type="button"
                        aria-label={hex}
                        aria-pressed={brandingForm.brandColor.toLowerCase() === hex}
                        title={hex}
                        onClick={() => { setBrandingError(null); setBrandingForm((f) => ({ ...f, brandColor: hex })); }}
                        className={cn(
                          'h-6 w-6 rounded-full border border-border/60 transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                          brandingForm.brandColor.toLowerCase() === hex && 'ring-2 ring-foreground ring-offset-2 ring-offset-background',
                        )}
                        style={{ background: hex }}
                      />
                    ))}
                  </div>
                  {brandingError && /colou?r/i.test(brandingError) && (
                    <p role="alert" className="text-xs text-destructive-text text-start">{brandingError}</p>
                  )}
                  {isHexColor(brandingForm.brandColor) && (
                    <div className="space-y-2">
                      <div className="flex flex-wrap gap-2">
                        {(['light', 'dark'] as const).map((mode) => (
                          <span
                            key={mode}
                            className="truncate rounded-md border px-2 py-1 text-xs font-bold"
                            style={{ background: sidebarBackgroundHex(mode), color: brandingForm.brandColor }}
                            title={t(mode === 'light' ? 'brandModeLight' : 'brandModeDark')}
                          >
                            {tenant?.name || t('brandPreviewName')}
                          </span>
                        ))}
                      </div>
                      {weakModes(brandingForm.brandColor).length > 0 && (
                        <p role="status" className="text-xs text-warning-text text-start">
                          {t('brandContrastWarn', {
                            modes: t(
                              weakModes(brandingForm.brandColor).length === 2
                                ? 'brandModeBoth'
                                : weakModes(brandingForm.brandColor)[0] === 'light'
                                  ? 'brandModeLight'
                                  : 'brandModeDark',
                            ),
                            ratio: Math.min(
                              ...weakModes(brandingForm.brandColor).map((m) => brandContrastOn(brandingForm.brandColor, m) ?? 21),
                            ).toFixed(1),
                          })}
                        </p>
                      )}
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">{t('brandColorHelp')}</p>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="brandTagline">{t('brandTaglineLabel')}</Label>
                <Input
                  id="brandTagline"
                  value={brandingForm.brandTagline}
                  onChange={(e) => { setBrandingError(null); setBrandingForm((f) => ({ ...f, brandTagline: e.target.value })); }}
                  placeholder="Quality You Can Trust"
                  maxLength={60}
                  aria-invalid={!!brandingError && /tagline/i.test(brandingError)}
                />
                {brandingError && /tagline/i.test(brandingError) && (
                  <p role="alert" className="text-xs text-destructive-text text-start">{brandingError}</p>
                )}
                <p className="text-xs text-muted-foreground">{t('brandTaglineHelp')}</p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
              <Button
                onClick={() => brandingMutation.mutate({
                  brandLogoUrl: brandingForm.brandLogoUrl || null,
                  brandColor: brandingForm.brandColor || null,
                  brandTagline: brandingForm.brandTagline || null,
                })}
                disabled={brandingMutation.isPending || !brandingDirty}
                variant="gradient"
                size="sm"
              >
                {brandingMutation.isPending ? t('savingLabel') : t('brandSave')}
              </Button>
              {brandingDirty && <span role="status" className="text-xs text-warning-text">{t('unsavedChanges')}</span>}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Receipt Customization */}
        {canToggle && (
          <Card className="animate-fade-in-up stagger-2 hover:shadow-glow-sm transition-all duration-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <ShieldCheck className="h-5 w-5" />
                {t('managerPinTitle')}
              </CardTitle>
              <p className="text-xs text-muted-foreground">{t('instantSaveCaption')}</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between rounded-lg border p-4">
                <div>
                  <p className="text-sm font-medium">{t('managerPinVoidsTitle')}</p>
                  <p className="text-xs text-muted-foreground">{t('managerPinVoidsDesc')}</p>
                </div>
                <Checkbox
                  checked={!!tenantData?.settings?.managerPinEnabled}
                  onChange={(e) => {
                    const settings = { managerPinEnabled: e.target.checked };
                    saveManagerSetting(settings);
                  }}
                />
              </div>
              <div className="flex items-center justify-between rounded-lg border p-4">
                <div>
                  <p className="text-sm font-medium">{t('outOfStockTitle')}</p>
                  <p className="text-xs text-muted-foreground">{t('outOfStockDesc')}</p>
                </div>
                <Checkbox
                  checked={tenantData?.settings?.blockOutOfStockSales !== false}
                  onChange={(e) => {
                    const settings = { blockOutOfStockSales: e.target.checked };
                    saveManagerSetting(settings);
                  }}
                />
              </div>
              <div className="rounded-lg border p-4 space-y-2">
                <p className="text-sm font-medium">{t('discountThresholdTitle')}</p>
                <p className="text-xs text-muted-foreground">{t('managerDiscountThresholdHelp')}</p>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    step={1}
                    className="w-24"
                    defaultValue={tenantData?.settings?.managerDiscountThreshold ?? 20}
                    onBlur={(e) => {
                      if (e.target.value.trim() === '') { e.target.value = String(tenantData?.settings?.managerDiscountThreshold ?? 20); return; }
                      const typed = Number(e.target.value);
                      if (!Number.isFinite(typed)) { e.target.value = String(tenantData?.settings?.managerDiscountThreshold ?? 20); return; }
                      const v = Math.max(0, Math.min(100, typed));
                      // POS-I295: the box shows the value that is saved, and says so when it differs from what was typed.
                      e.target.value = String(v);
                      if (v !== typed) toast(t('thresholdAdjusted', { value: v }));
                      const settings = { managerDiscountThreshold: v };
                      saveManagerSetting(settings);
                    }}
                  />
                  <span className="text-sm text-muted-foreground">%</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Receipt Customization */}
        {canToggle && (
          <Card className="animate-fade-in-up stagger-2 hover:shadow-glow-sm transition-all duration-200 xl:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Receipt className="h-5 w-5" />
                {t('receiptCustomizationTitle')}
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_300px]">
              <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium">{t('footerMessage')}</Label>
                <p className="text-xs text-muted-foreground mb-1.5">{t('footerMessageDesc')} {t('footerNewLineHint')}</p>
                <Textarea
                  className="min-h-[80px] resize-none"
                  placeholder={t('footerPlaceholder')}
                  value={receiptFooter}
                  onChange={(e) => { setReceiptError(null); setReceiptFooter(e.target.value); }}
                  maxLength={300}
                  aria-invalid={!!receiptError && /footer/i.test(receiptError)}
                />
                {receiptError && /footer/i.test(receiptError) && (
                  <p role="alert" className="text-xs text-destructive-text text-start">{receiptError}</p>
                )}
                <p className="text-xs text-muted-foreground text-end">{receiptFooter.length}/300</p>
              </div>
              <div>
                <Label className="text-sm font-medium">{t('logoUrl')}</Label>
                <p className="text-xs text-muted-foreground mb-1.5">{t('logoUrlDesc')}</p>
                <LogoUploadField
                  id="receiptLogoUrl"
                  value={receiptLogoUrl}
                  onChange={setReceiptLogoUrl}
                  onClearError={() => setReceiptError(null)}
                  error={receiptError && /logo/i.test(receiptError) ? receiptError : null}
                  placeholder="https://yoursite.com/logo.png"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">{t('headerNote')}</Label>
                <p className="text-xs text-muted-foreground mb-1.5">{t('headerNoteDesc')}</p>
                <Input
                  placeholder={t('headerNotePlaceholder')}
                  value={receiptLayout.headerNote}
                  onChange={(e) => { setReceiptError(null); setReceiptLayout((l) => ({ ...l, headerNote: e.target.value })); }}
                  maxLength={120}
                  aria-invalid={!!receiptError && /header note/i.test(receiptError)}
                />
                {receiptError && /header note/i.test(receiptError) && (
                  <p role="alert" className="text-xs text-destructive-text text-start">{receiptError}</p>
                )}
                <p className="text-xs text-muted-foreground text-end">{receiptLayout.headerNote.length}/120</p>
              </div>
              <fieldset>
                <legend className="text-sm font-medium">{t('showOnReceipt')}</legend>
                <p className="text-xs text-muted-foreground mb-2">{t('showOnReceiptDesc')}</p>
                <div className="grid grid-cols-1 gap-x-6 gap-y-2 xl:grid-cols-2">
                  {RECEIPT_SHOW_FLAGS.map((flag) => (
                    <label key={flag} className="flex items-start gap-2 text-sm">
                      <Checkbox
                        className="mt-0.5"
                        checked={receiptLayout[flag]}
                        onChange={(e) => setReceiptLayout((l) => ({ ...l, [flag]: e.target.checked }))}
                      />
                      <span>
                        {t(flag)}
                        {flag === 'showFbrQr' && <span className="block text-xs text-muted-foreground">{t('showFbrQrHelp')}</span>}
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>
              <div>
                <Label className="text-sm font-medium">{t('returnPolicy')}</Label>
                <p className="text-xs text-muted-foreground mb-1.5">{t('returnPolicyDesc')}</p>
                <Textarea
                  className="min-h-[80px] resize-none"
                  placeholder={t('returnPolicyPlaceholder')}
                  value={receiptLayout.returnPolicy}
                  onChange={(e) => { setReceiptError(null); setReceiptLayout((l) => ({ ...l, returnPolicy: e.target.value })); }}
                  maxLength={300}
                  aria-invalid={!!receiptError && /return policy/i.test(receiptError)}
                />
                {receiptError && /return policy/i.test(receiptError) && (
                  <p role="alert" className="text-xs text-destructive-text text-start">{receiptError}</p>
                )}
                <p className="text-xs text-muted-foreground text-end">{receiptLayout.returnPolicy.length}/300</p>
              </div>
              <p className="text-xs text-muted-foreground">{t('receiptLayoutNote')}</p>
              <Button
                onClick={() => receiptMutation.mutate({ receiptFooter, receiptLogoUrl, receiptLayout })}
                disabled={receiptMutation.isPending || !receiptDirty}
                className="gap-2"
              >
                {receiptSaved ? <><CheckCircle2 className="h-4 w-4 text-success-text" /> {t('savedLabel')}</> : receiptMutation.isPending ? t('savingLabel') : t('saveReceiptSettings')}
              </Button>
              {receiptDirty && <span role="status" className="ms-3 text-xs text-warning-text">{t('unsavedChanges')}</span>}
              <p className="text-xs text-muted-foreground">
                {t('printerPerTill')}{' '}
                <button type="button" className="text-primary underline-offset-2 hover:underline" onClick={() => setSettingsTab('integrations')}>
                  {t('printerPerTillLink')}
                </button>
              </p>
              </div>
              <ReceiptPreview
                logoUrl={receiptLogoUrl || tenantData?.brandLogoUrl || ''}
                name={tenantData?.name ?? tenant?.name}
                tagline={tenantData?.brandTagline}
                ntn={formatSellerNtn(fbrSettingsData?.fbrNtn)}
                footer={receiptFooter}
                layout={receiptLayout}
              />
            </CardContent>
          </Card>
        )}

        {/* Data Environment */}
        <Card className={`animate-fade-in-up stagger-3 hover:shadow-glow-sm transition-all duration-200 ${isTestMode ? 'border-warning/30' : 'border-success/30'}`}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <FlaskConical className={`h-5 w-5 ${isTestMode ? 'text-warning-text' : 'text-success-text'}`} />
              {t('dataEnvironment')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className={`rounded-xl border-2 p-4 ${isTestMode ? 'border-warning/30 bg-warning/10' : 'border-success/30 bg-success/10'}`}>
              <div className="flex items-center gap-2">
                {isTestMode ? (
                  <FlaskConical className="h-5 w-5 text-warning-text" />
                ) : (
                  <CheckCircle2 className="h-5 w-5 text-success-text" />
                )}
                <p className={`font-semibold ${isTestMode ? 'text-warning-text' : 'text-success-text'}`}>
                  {isTestMode ? t('testModeActive') : t('productionModeActive')}
                </p>
              </div>
              <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed">
                {isTestMode
                  ? t('testModeNote')
                  : t('productionModeNote')}
              </p>
            </div>
            {canToggle && (
              <div className="flex flex-wrap gap-3">
                <Button
                  variant={isTestMode ? 'outline' : 'default'}
                  size="sm"
                  className={isTestMode ? '' : 'bg-success-solid hover:bg-success-solid/90 text-success-solid-foreground'}
                  disabled={!isTestMode || toggleMutation.isPending}
                  onClick={() => setConfirmTestMode(true)}
                >
                  <CheckCircle2 className="me-1.5 h-3.5 w-3.5" />
                  {t('productionBtn')}
                </Button>
                <Button
                  variant={isTestMode ? 'default' : 'outline'}
                  size="sm"
                  className={isTestMode ? 'bg-warning hover:bg-warning/90 text-warning-foreground' : ''}
                  disabled={isTestMode || toggleMutation.isPending}
                  onClick={() => setConfirmTestMode(true)}
                >
                  <FlaskConical className="me-1.5 h-3.5 w-3.5" />
                  {t('testModeBtn')}
                </Button>
              </div>
            )}
            {canToggle && isTestMode && (
              <div className="border-t pt-4">
                <p className="text-xs text-muted-foreground mb-3">
                  {t('clearTestDataDesc')}
                </p>
                <Button
                  variant="destructive"
                  size="sm"
                  disabled={resetTestDataMutation.isPending}
                  onClick={() => setConfirmReset(true)}
                >
                  <Trash2 className="me-1.5 h-3.5 w-3.5" />
                  {resetTestDataMutation.isPending ? t('clearingLabel') : t('clearAllTestData')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Appearance (POS-I226) */}
        <AppearanceCard />
        </>)}

        {settingsTab === 'tax' && (<>
        {/* FBR Integration */}
        {hasPermission('fbr:*') || hasPermission('fbr:manage') ? (
        <Card className="animate-fade-in-up stagger-5 hover:shadow-glow-sm transition-all duration-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Zap className="h-5 w-5 text-warning-text" />
              {t('fbrIntegrationTitle')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {t('fbrIntegrationDesc')}
            </p>
            <Link to="/finance/fbr">
              <Button variant="outline" className="w-full gap-2">
                <ExternalLink className="h-4 w-4" />
                {t('manageFbrSettings')}
              </Button>
            </Link>
          </CardContent>
        </Card>
        ) : null}

        {/* Payment Method Tax Rules */}
        {canToggle && (
        <Card className="animate-fade-in-up stagger-5 hover:shadow-glow-sm transition-all duration-200 xl:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <CreditCard className="h-5 w-5 text-info-text" />
              {t('pmtaxTitle')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t('taxPmDescA')} <strong>{t('taxPmDescB')}</strong> {t('taxPmDescC')}{' '}
              {t('taxPmLeaveA')} <strong>{t('taxPmLeaveB')}</strong> {t('taxPmLeaveC')}
            </p>

            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <p className="text-sm font-medium">{t(paymentMethodEnabled ? 'pmtaxEnabled' : 'pmtaxDisabled')}</p>
                <p className="text-xs text-muted-foreground">{t(paymentMethodEnabled ? 'pmtaxEnabledSub' : 'pmtaxDisabledSub')}</p>
              </div>
              {paymentMethodEnabled ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive-text border-destructive/30 hover:bg-destructive/10"
                  disabled={taxRuleMutation.isPending}
                  onClick={() => {
                    setPmRules(null);
                    taxRuleMutation.mutate({ paymentMethodTaxRules: null });
                  }}
                >
                  {t('disable')}
                </Button>
              ) : (
                <Button variant="outline" size="sm" onClick={initPaymentRules}>
                  {t('enableAndConfigure')}
                </Button>
              )}
            </div>

            {editingPaymentTax && pmRules && (
              <div className="space-y-3 rounded-lg border p-4">
                <p className="text-sm font-semibold">{t('configureRatesLabel')}</p>
                {PAYMENT_METHODS.map((method) => (
                  <div key={method} className="grid grid-cols-3 gap-3 items-center">
                    <span className="text-sm font-medium">{t(PAYMENT_METHOD_LABEL_KEYS[method])}</span>
                    <div className="flex items-center gap-1.5">
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        step={0.5}
                        className="w-20"
                        value={pmRules[method]?.rate ?? 0}
                        onChange={(e) =>
                          setPmRules((prev) => prev ? ({
                            ...prev,
                            [method]: { ...prev[method], rate: parseFloat(e.target.value) || 0 },
                          }) : prev)
                        }
                      />
                      <span className="text-sm text-muted-foreground">%</span>
                    </div>
                    <Input
                      type="text"
                      placeholder={t('taxLabelPlaceholder')}
                      value={pmRules[method]?.name ?? ''}
                      onChange={(e) =>
                        setPmRules((prev) => prev ? ({
                          ...prev,
                          [method]: { ...prev[method], name: e.target.value },
                        }) : prev)
                      }
                    />
                  </div>
                ))}
                <div className="flex gap-2 pt-2">
                  <Button
                    size="sm"
                    disabled={taxRuleMutation.isPending}
                    onClick={() =>
                      taxRuleMutation.mutate({
                        paymentMethodTaxRules: Object.fromEntries(
                          PAYMENT_METHODS.map((m) => [m, { ...pmRules[m], overridesProductTax: true }]),
                        ),
                      })
                    }
                  >
                    {t('saveRules')}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => { setEditingPaymentTax(false); setPmRules(null); }}>
                    {t('cancel')}
                  </Button>
                </div>
              </div>
            )}

            {paymentMethodEnabled && !editingPaymentTax && currentPmRules && (
              <div className="space-y-2">
                {(Object.entries(currentPmRules) as [PaymentMethod, PaymentMethodTaxRule][]).map(([method, rule]) => (
                  <div key={method} className="flex items-center justify-between rounded-lg bg-muted/40 px-3 py-2 text-sm">
                    <span className="font-medium">{t(PAYMENT_METHOD_LABEL_KEYS[method])}</span>
                    <span className="text-muted-foreground">{rule.name}</span>
                    <span className="font-semibold">{rule.rate}%</span>
                  </div>
                ))}
                <Button variant="outline" size="sm" className="mt-1" onClick={initPaymentRules}>
                  {t('editRules')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {/* ATL Further Tax Settings */}
        {canToggle && (
        <Card className="animate-fade-in-up stagger-5 hover:shadow-glow-sm transition-all duration-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Users className="h-5 w-5 text-primary-text" />
              {t('atlFurtherTaxTitle')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t('taxAtlDescA')} <strong>{t('taxAtlDescB')}</strong> {t('taxAtlDescC')}{' '}
              {t('taxPmLeaveA')} <strong>{t('taxPmLeaveB')}</strong> {t('taxAtlLeaveC')}
            </p>

            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <p className="text-sm font-medium">{t(currentAtlEnabled ? 'atlFurtherTaxEnabled' : 'atlFurtherTaxDisabled')}</p>
                <p className="text-xs text-muted-foreground">{t(currentAtlEnabled ? 'atlFurtherTaxEnabledSub' : 'atlFurtherTaxDisabledSub')}</p>
              </div>
              {currentAtlEnabled ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive-text border-destructive/30 hover:bg-destructive/10"
                  disabled={taxRuleMutation.isPending}
                  onClick={() => {
                    setAtlEnabled(false);
                    taxRuleMutation.mutate({ atlEnabled: false });
                  }}
                >
                  {t('disable')}
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={taxRuleMutation.isPending}
                  onClick={() => {
                    setAtlEnabled(true);
                    taxRuleMutation.mutate({ atlEnabled: true });
                  }}
                >
                  {t('enable')}
                </Button>
              )}
            </div>

            {taxRuleLoading ? (
              <p className="text-sm text-muted-foreground">{t('loadingLabel')}</p>
            ) : currentAtlEnabled ? (
              <div className="space-y-4">
                <div className="rounded-lg border p-4 space-y-3">
                  <div>
                    <p className="text-sm font-medium mb-1">{t('furtherTaxRateLabel')}</p>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min={0}
                        max={20}
                        step={0.5}
                        className="w-24"
                        value={currentFurtherTaxRate}
                        onChange={(e) => setFurtherTaxRate(parseFloat(e.target.value) || 0)}
                      />
                      <span className="text-sm text-muted-foreground">{t('furtherTaxStandard')}</span>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium mb-1">{t('defaultBuyerAtlLabel')}</p>
                    <div className="flex gap-2">
                      <button
                        className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${!currentDefaultAtl ? 'bg-destructive/10 border-destructive/30 text-destructive-text' : 'border-transparent hover:bg-muted/30 text-muted-foreground'}`}
                        onClick={() => setDefaultAtl(false)}
                      >
                        {t('nonAtlBtn')}
                      </button>
                      <button
                        className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${currentDefaultAtl ? 'bg-success/10 border-success/30 text-success-text' : 'border-transparent hover:bg-muted/30 text-muted-foreground'}`}
                        onClick={() => setDefaultAtl(true)}
                      >
                        {t('atlBtn')}
                      </button>
                    </div>
                    <p className="mt-1.5 text-xs text-muted-foreground">
                      {t('atlHintNonAtl')}
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  disabled={taxRuleMutation.isPending}
                  onClick={() =>
                    taxRuleMutation.mutate({
                      atlEnabled: currentAtlEnabled,
                      defaultBuyerAtlStatus: currentDefaultAtl,
                      furtherTaxRate: currentFurtherTaxRate,
                    })
                  }
                >
                  {t('saveAtlSettings')}
                </Button>
              </div>
            ) : null}
          </CardContent>
        </Card>
        )}
        </>)}

        {settingsTab === 'team' && (<>
        {/* Permissions */}
        <Card className="animate-fade-in-up stagger-5 hover:shadow-glow-sm transition-all duration-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Shield className="h-5 w-5" />
              {t('permissionsTitle')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-1.5">
              {user?.permissions?.map((p: string) => {
                const meta = PERMISSION_LABELS[p];
                const label = meta ? `${t(meta.module)}: ${t(meta.label)}` : p;
                return (
                  <span key={p} className="inline-flex items-center rounded-md bg-primary/10 border border-primary/15 px-2 py-1 text-xs text-muted-foreground">
                    {label}
                  </span>
                );
              })}
              {(!user?.permissions || user.permissions.length === 0) && (
                <p className="text-sm text-muted-foreground">{t('noPermissions')}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* SMS Notifications */}
        {canToggle && (
        <Card className="animate-fade-in-up stagger-5 hover:shadow-glow-sm transition-all duration-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Bell className="h-5 w-5 text-info-text" />
              {t('smsNotificationsTitle')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t('smsReceiptsIntro')}
            </p>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <p className="text-sm font-medium">{t(smsReceiptsEnabled ? 'smsEnabled' : 'smsDisabled')}</p>
                <p className="text-xs text-muted-foreground">{t(smsReceiptsEnabled ? 'smsEnabledSub' : 'smsDisabledSub')}</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                disabled={smsMutation.isPending}
                onClick={() => smsMutation.mutate(!smsReceiptsEnabled)}
              >
                {smsReceiptsEnabled ? t('disable') : t('enable')}
              </Button>
            </div>
            <Button variant="outline" size="sm" className="gap-2" onClick={() => setSmsTestOpen(true)}>
              <Bell className="h-4 w-4" /> {t('testSms')}
            </Button>
          </CardContent>
        </Card>
        )}

        {/* PO Approval Workflow */}
        {canToggle && hasPermission('purchases:*') && (
        <Card className="animate-fade-in-up stagger-5 hover:shadow-glow-sm transition-all duration-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <ShoppingBag className="h-5 w-5 text-warning-text" />
              {t('poApprovalTitle')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t('poApprovalIntro')}
            </p>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <p className="text-sm font-medium">{t(requirePoApproval ? 'poApprovalRequired' : 'poApprovalNotRequired')}</p>
                <p className="text-xs text-muted-foreground">{t(requirePoApproval ? 'poApprovalRequiredSub' : 'poApprovalNotRequiredSub')}</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                disabled={poApprovalMutation.isPending}
                onClick={() => poApprovalMutation.mutate(!requirePoApproval)}
              >
                {requirePoApproval ? t('disable') : t('enable')}
              </Button>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-lg border p-3 space-y-2">
                <p className="text-sm font-medium">{t('expenseApprovalThresholdTitle')}</p>
                <p className="text-xs text-muted-foreground">{t('expenseApprovalThresholdSub')}</p>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={0}
                    max={9999999999}
                    step="0.01"
                    className="h-8 w-40"
                    defaultValue={Number(tenantData?.expenseApprovalThreshold ?? 0)}
                    onBlur={(e) => {
                      const v = Number(e.target.value);
                      if (!(v >= 0 && v <= 9999999999)) {
                        toast.error(t('thresholdRange', { defaultValue: 'Enter an amount from 0 to 9,999,999,999' }));
                        e.target.value = String(Number(tenantData?.expenseApprovalThreshold ?? 0));
                      } else if (v !== Number(tenantData?.expenseApprovalThreshold ?? 0)) {
                        approvalThresholdMutation.mutate({ expenseApprovalThreshold: v });
                      }
                    }}
                  />
                  <span className="text-xs text-muted-foreground">{t('zeroDisables')}</span>
                </div>
              </div>
              <div className="rounded-lg border p-3 space-y-2">
                <p className="text-sm font-medium">{t('vendorPaymentApprovalThresholdTitle')}</p>
                <p className="text-xs text-muted-foreground">{t('vendorPaymentApprovalThresholdSub')}</p>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={0}
                    max={9999999999}
                    step="0.01"
                    className="h-8 w-40"
                    defaultValue={Number(tenantData?.vendorPaymentApprovalThreshold ?? 0)}
                    onBlur={(e) => {
                      const v = Number(e.target.value);
                      if (!(v >= 0 && v <= 9999999999)) {
                        toast.error(t('thresholdRange', { defaultValue: 'Enter an amount from 0 to 9,999,999,999' }));
                        e.target.value = String(Number(tenantData?.vendorPaymentApprovalThreshold ?? 0));
                      } else if (v !== Number(tenantData?.vendorPaymentApprovalThreshold ?? 0)) {
                        approvalThresholdMutation.mutate({ vendorPaymentApprovalThreshold: v });
                      }
                    }}
                  />
                  <span className="text-xs text-muted-foreground">{t('zeroDisables')}</span>
                </div>
              </div>
              <div className="rounded-lg border p-3 space-y-2">
                <p className="text-sm font-medium">{t('refundApprovalThresholdTitle')}</p>
                <p className="text-xs text-muted-foreground">{t('refundApprovalThresholdSub')}</p>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={0}
                    max={9999999999}
                    step="0.01"
                    className="h-8 w-40"
                    defaultValue={Number(tenantData?.refundApprovalThreshold ?? 0)}
                    onBlur={(e) => {
                      const v = Number(e.target.value);
                      if (!(v >= 0 && v <= 9999999999)) {
                        toast.error(t('thresholdRange', { defaultValue: 'Enter an amount from 0 to 9,999,999,999' }));
                        e.target.value = String(Number(tenantData?.refundApprovalThreshold ?? 0));
                      } else if (v !== Number(tenantData?.refundApprovalThreshold ?? 0)) {
                        approvalThresholdMutation.mutate({ refundApprovalThreshold: v });
                      }
                    }}
                  />
                  <span className="text-xs text-muted-foreground">{t('zeroDisables')}</span>
                </div>
              </div>
              <div className="rounded-lg border p-3 space-y-2">
                <p className="text-sm font-medium">{t('tipApprovalThresholdTitle')}</p>
                <p className="text-xs text-muted-foreground">{t('tipApprovalThresholdSub')}</p>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={0}
                    max={9999999999}
                    step="0.01"
                    className="h-8 w-40"
                    defaultValue={Number(tenantData?.tipApprovalThreshold ?? 0)}
                    onBlur={(e) => {
                      const v = Number(e.target.value);
                      if (!(v >= 0 && v <= 9999999999)) {
                        toast.error(t('thresholdRange', { defaultValue: 'Enter an amount from 0 to 9,999,999,999' }));
                        e.target.value = String(Number(tenantData?.tipApprovalThreshold ?? 0));
                      } else if (v !== Number(tenantData?.tipApprovalThreshold ?? 0)) {
                        approvalThresholdMutation.mutate({ tipApprovalThreshold: v });
                      }
                    }}
                  />
                  <span className="text-xs text-muted-foreground">{t('zeroDisables')}</span>
                </div>
              </div>
              <div className="rounded-lg border p-3 space-y-2">
                <p className="text-sm font-medium">{t('cashVarianceToleranceTitle', { defaultValue: 'Cash variance tolerance' })}</p>
                <p className="text-xs text-muted-foreground">{t('cashVarianceToleranceSub', { defaultValue: 'A cashier closing a shift short or over by more than this amount must give a reason and managers are notified.' })}</p>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={0}
                    max={9999999999}
                    step="0.01"
                    className="h-8 w-40"
                    defaultValue={Number(tenantData?.cashVarianceTolerance ?? 0)}
                    onBlur={(e) => {
                      const v = Number(e.target.value);
                      if (!(v >= 0 && v <= 9999999999)) {
                        toast.error(t('thresholdRange', { defaultValue: 'Enter an amount from 0 to 9,999,999,999' }));
                        e.target.value = String(Number(tenantData?.cashVarianceTolerance ?? 0));
                      } else if (v !== Number(tenantData?.cashVarianceTolerance ?? 0)) {
                        approvalThresholdMutation.mutate({ cashVarianceTolerance: v });
                      }
                    }}
                  />
                  <span className="text-xs text-muted-foreground">{t('zeroEveryDifference', { defaultValue: '0 = every difference needs a reason' })}</span>
                </div>
              </div>
              {/* POS-I92: blind cash count (count first, reveal after). */}
              <div className="rounded-lg border p-3 flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium">{t('blindCashCountTitle')}</p>
                  <p className="text-xs text-muted-foreground">{t('blindCashCountSub')}</p>
                </div>
                <Button
                  size="sm"
                  variant={tenantData?.blindCashCount ? 'outline' : 'default'}
                  disabled={blindCountMutation.isPending}
                  onClick={() => blindCountMutation.mutate(!tenantData?.blindCashCount)}
                >
                  {tenantData?.blindCashCount ? t('disable', { defaultValue: 'Disable' }) : t('enable', { defaultValue: 'Enable' })}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
        )}

        {/* Dining service-charge destination (T-380) */}
        {canToggle && (
        <Card className="animate-fade-in-up stagger-5 hover:shadow-glow-sm transition-all duration-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <UtensilsCrossed className="h-5 w-5 text-warning-text" />
              {t('serviceChargePoolTitle')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t('serviceChargePoolDesc')}
            </p>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <p className="text-sm font-medium">
                  {t(serviceChargeIsStaffPool ? 'serviceChargePoolOn' : 'serviceChargePoolOff')}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t(serviceChargeIsStaffPool ? 'serviceChargePoolOnSub' : 'serviceChargePoolOffSub')}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                disabled={serviceChargePoolMutation.isPending}
                onClick={() => serviceChargePoolMutation.mutate(!serviceChargeIsStaffPool)}
              >
                {t(serviceChargeIsStaffPool ? 'disable' : 'enable')}
              </Button>
            </div>
          </CardContent>
        </Card>
        )}

        {/* Inventory / Costing (T-203) */}
        {canToggle && (
        <Card className="animate-fade-in-up stagger-5 hover:shadow-glow-sm transition-all duration-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Boxes className="h-5 w-5 text-warning-text" />
              {t('costingTitle')}
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">{t('costingDesc')}</p>
          </CardHeader>
          <CardContent className="space-y-4">
            {!costingAddonActive ? (
              <div className="flex items-start gap-3 rounded-xl border border-border bg-muted/20 p-3.5 opacity-90">
                <div className="mt-0.5 rounded-lg bg-muted p-1.5">
                  <Lock className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm font-medium leading-none">{t('costingLockedTitle')}</p>
                    <span className="text-xs text-muted-foreground">{t('lockedLabel')}</span>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground leading-relaxed">{t('costingLockedDesc')}</p>
                  <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="mt-1.5 inline-flex items-center gap-0.5 text-xs font-medium text-success-text hover:underline">
                    {t('unlockFeature')} <ChevronRight className="h-3 w-3" />
                  </a>
                </div>
              </div>
            ) : costingSettingsLoading ? (
              <p className="text-sm text-muted-foreground">{t('loadingSettings')}</p>
            ) : (
              <div className="rounded-lg border p-3 space-y-3">
                <div>
                  <p className="text-sm font-medium">{t('costingCurrentMethodLabel')}</p>
                  <p className="text-xs text-muted-foreground">
                    {costingSettings?.costMethod === 'FIFO' ? t('costingMethodFifoDesc') : t('costingMethodWacDesc')}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant={costingSettings?.costMethod === 'WAC' ? 'default' : 'outline'}
                    size="sm"
                    disabled={!canCostingSettings || costingSwitchMutation.isPending || costingSettings?.costMethod === 'WAC'}
                    onClick={() => setCostingSwitchTarget('WAC')}
                  >
                    {t('costingMethodWac')}
                  </Button>
                  <Button
                    variant={costingSettings?.costMethod === 'FIFO' ? 'default' : 'outline'}
                    size="sm"
                    disabled={!canCostingSettings || costingSwitchMutation.isPending || costingSettings?.costMethod === 'FIFO'}
                    onClick={() => setCostingSwitchTarget('FIFO')}
                  >
                    {t('costingMethodFifo')}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  {t('costingOnHandValueLabel')}: <span className="font-medium text-foreground">{formatCurrency(costingSettings?.onHandValue ?? 0)}</span>
                </p>
              </div>
            )}
          </CardContent>
        </Card>
        )}

        <ConfirmDialog
          open={costingSwitchTarget !== null}
          onCancel={() => setCostingSwitchTarget(null)}
          title={t('costingSwitchConfirmTitle')}
          variant="warning"
          description={t('costingSwitchConfirmDesc', {
            method: costingSwitchTarget === 'FIFO' ? t('costingMethodFifo') : t('costingMethodWac'),
            value: formatCurrency(costingSettings?.onHandValue ?? 0),
          })}
          confirmLabel={t('costingSwitchButton')}
          loading={costingSwitchMutation.isPending}
          onConfirm={() => costingSwitchTarget && costingSwitchMutation.mutate(costingSwitchTarget)}
        />

        {/* Plan usage & limits + add-on requests (T-178) */}
        {hasPermission('settings:read') && <MyPlanUsageCard />}

        {/* Settings change history (T-176) */}
        {hasPermission('audit:read') && <SettingsHistoryCard />}

        {/* Communications */}
        {canToggle && (
        <Card className="animate-fade-in-up stagger-5 hover:shadow-glow-sm transition-all duration-200 xl:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Wifi className="h-5 w-5 text-primary-text" />
              {t('communicationsTitle')}
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">{t('communicationsDesc')}</p>
          </CardHeader>
          <CardContent className="space-y-6">
            {commLoading ? (
              <p className="text-sm text-muted-foreground">{t('loadingSettings')}</p>
            ) : (
              <>
                {/* Customer Portal URL */}
                <div className="rounded-xl bg-primary/10 border border-primary/30 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Globe className="h-4 w-4 text-primary-text" />
                    <p className="text-sm font-semibold text-primary-text">{t('customerPortalUrlLabel')}</p>
                  </div>
                  <div className="flex items-center gap-2 bg-background rounded-lg border px-3 py-2">
                    <span className="text-xs font-mono flex-1 truncate text-muted-foreground">{commData?.portalUrl ?? '—'}</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 shrink-0"
                      onClick={() => {
                        if (commData?.portalUrl) {
                          navigator.clipboard.writeText(commData.portalUrl);
                          setCopiedPortal(true);
                          setTimeout(() => setCopiedPortal(false), 2000);
                        }
                      }}
                    >
                      {copiedPortal ? <CheckCheck className="h-3.5 w-3.5 text-success-text" /> : <Copy className="h-3.5 w-3.5" />}
                    </Button>
                  </div>
                  <p className="text-xs text-primary-text/60 mt-1.5">
                    {t('portalShareHint')}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Email */}
                  <div className="rounded-xl border p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-info-text" />
                        <p className="text-sm font-semibold">{t('emailTitle')}</p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${commData?.email?.enabled ? 'bg-success/10 text-success-text' : 'bg-muted text-muted-foreground'}`}>
                        {commData?.email?.enabled ? t('enabledStatus') : t('disabledStatus')}
                      </span>
                    </div>
                    <div className="space-y-2">
                      <div>
                        <Label className="text-xs">{t('smtpHostLabel')}</Label>
                        <Input
                          className="h-8 text-xs"
                          placeholder={commData?.email?.smtpHost || 'smtp.gmail.com'}
                          defaultValue={commData?.email?.smtpHost}
                          onChange={(e) => setEmailForm((f) => ({ ...f, smtpHost: e.target.value }))}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs">{t('portLabel')}</Label>
                          <Input
                            className="h-8 text-xs"
                            type="number"
                            placeholder="587"
                            defaultValue={commData?.email?.smtpPort}
                            onChange={(e) => setEmailForm((f) => ({ ...f, smtpPort: Number(e.target.value) }))}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">{t('secureTlsLabel')}</Label>
                          <div className="flex items-center h-8 gap-2 mt-1">
                            <Checkbox
                              defaultChecked={commData?.email?.smtpSecure}
                              onChange={(e) => setEmailForm((f) => ({ ...f, smtpSecure: e.target.checked }))}
                            />
                            <span className="text-xs text-muted-foreground">{t('useTlsLabel')}</span>
                          </div>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs">{t('usernameLabel')}</Label>
                        <Input
                          className="h-8 text-xs"
                          placeholder="your@email.com"
                          defaultValue={commData?.email?.smtpUser}
                          onChange={(e) => setEmailForm((f) => ({ ...f, smtpUser: e.target.value }))}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">{t('commPasswordLabel')}</Label>
                        <Input
                          className="h-8 text-xs"
                          type="password"
                          placeholder={commData?.email?.smtpPass ? '••••••••' : t('commAppPasswordPlaceholder')}
                          onChange={(e) => setEmailForm((f) => ({ ...f, smtpPass: e.target.value }))}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">{t('fromEmailLabel')}</Label>
                        <Input
                          className="h-8 text-xs"
                          placeholder="noreply@yourbusiness.com"
                          defaultValue={commData?.email?.fromEmail}
                          onChange={(e) => setEmailForm((f) => ({ ...f, fromEmail: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 h-8 text-xs"
                        onClick={() => commMutation.mutate({ email: { ...emailForm, enabled: !commData?.email?.enabled } })}
                      >
                        {commData?.email?.enabled ? t('disable') : t('enable')}
                      </Button>
                      <Button
                        size="sm"
                        className="flex-1 h-8 text-xs"
                        disabled={commMutation.isPending || Object.keys(emailForm).length === 0}
                        onClick={() => commMutation.mutate({ email: emailForm })}
                      >
                        {t('save')}
                      </Button>
                    </div>
                  </div>

                  {/* SMS */}
                  <div className="rounded-xl border p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-success-text" />
                        <p className="text-sm font-semibold">{t('smsTitle')}</p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${commData?.sms?.enabled ? 'bg-success/10 text-success-text' : 'bg-muted text-muted-foreground'}`}>
                        {commData?.sms?.enabled ? t('enabledStatus') : t('disabledStatus')}
                      </span>
                    </div>
                    <div className="space-y-2">
                      <div>
                        <Label className="text-xs">{t('providerLabel')}</Label>
                        <Dropdown
                          className="w-full"
                          value={smsCommForm.provider ?? commData?.sms?.provider ?? 'twilio'}
                          onChange={(e) => setSmsCommForm((f) => ({ ...f, provider: e.value }))}
                          options={[
                            { label: 'Twilio', value: 'twilio' },
                            { label: t('commHttpWebhook'), value: 'http_webhook' },
                          ]}
                          optionLabel="label"
                          optionValue="value"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">{t('accountSidLabel')}</Label>
                        <Input
                          className="h-8 text-xs"
                          placeholder="AC..."
                          defaultValue={commData?.sms?.accountSid}
                          onChange={(e) => setSmsCommForm((f) => ({ ...f, accountSid: e.target.value }))}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">{t('authTokenLabel')}</Label>
                        <Input
                          className="h-8 text-xs"
                          type="password"
                          placeholder={commData?.sms?.authToken ? '••••••••' : t('commTwilioTokenPlaceholder')}
                          onChange={(e) => setSmsCommForm((f) => ({ ...f, authToken: e.target.value }))}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">{t('fromNumberLabel')}</Label>
                        <Input
                          className="h-8 text-xs"
                          placeholder="+1234567890"
                          defaultValue={commData?.sms?.fromNumber}
                          onChange={(e) => setSmsCommForm((f) => ({ ...f, fromNumber: e.target.value }))}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">{t('webhookUrlLabel')}</Label>
                        <Input
                          className="h-8 text-xs"
                          placeholder="https://your-sms-api.com/send"
                          defaultValue={commData?.sms?.webhookUrl}
                          onChange={(e) => setSmsCommForm((f) => ({ ...f, webhookUrl: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 h-8 text-xs"
                          onClick={() => commMutation.mutate({ sms: { ...smsCommForm, enabled: !commData?.sms?.enabled } })}
                        >
                          {commData?.sms?.enabled ? t('disable') : t('enable')}
                        </Button>
                        <Button
                          size="sm"
                          className="flex-1 h-8 text-xs"
                          disabled={commMutation.isPending || Object.keys(smsCommForm).length === 0}
                          onClick={() => commMutation.mutate({ sms: smsCommForm })}
                        >
                          {t('save')}
                        </Button>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full h-8 text-xs gap-1"
                        onClick={() => setSmsTestOpen(true)}
                      >
                        <Phone className="h-3 w-3" /> {t('testSms')}
                      </Button>
                    </div>
                  </div>

                  {/* WhatsApp */}
                  <div className="rounded-xl border p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <MessageCircle className="h-4 w-4 text-success-text" />
                        <p className="text-sm font-semibold">{t('whatsappTitle')}</p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${commData?.whatsapp?.enabled ? 'bg-success/10 text-success-text' : 'bg-muted text-muted-foreground'}`}>
                        {commData?.whatsapp?.enabled ? t('enabledStatus') : t('disabledStatus')}
                      </span>
                    </div>
                    <div className="space-y-2">
                      <div className="rounded-lg bg-success/10 border border-success/30 p-3">
                        <p className="text-xs text-success-text font-medium mb-1">{t('waTwoModes')}</p>
                        <p className="text-xs text-success-text/70">
                          <span className="font-semibold">{t('waApiModeLabel')}</span> {t('waApiModeText')}
                        </p>
                        <p className="text-xs text-success-text/70 mt-1">
                          <span className="font-semibold">{t('waLinkModeLabel')}</span> {t('waLinkModeText')}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="wa-use-api"
                          defaultChecked={commData?.whatsapp?.useApi}
                          onChange={(e) => setWaForm((f) => ({ ...f, useApi: e.target.checked }))}
                        />
                        <Label htmlFor="wa-use-api" className="text-xs">{t('useTwilioApiLabel')}</Label>
                      </div>
                      <div>
                        <Label className="text-xs">{t('whatsappBusinessNumberLabel')}</Label>
                        <Input
                          className="h-8 text-xs"
                          placeholder="whatsapp:+14155551234"
                          defaultValue={commData?.whatsapp?.fromNumber}
                          onChange={(e) => setWaForm((f) => ({ ...f, fromNumber: e.target.value }))}
                        />
                        <p className="text-xs text-muted-foreground mt-1">{t('waSenderHint')}</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 h-8 text-xs"
                          onClick={() => commMutation.mutate({ whatsapp: { ...waForm, enabled: !commData?.whatsapp?.enabled } })}
                        >
                          {commData?.whatsapp?.enabled ? t('disable') : t('enable')}
                        </Button>
                        <Button
                          size="sm"
                          className="flex-1 h-8 text-xs"
                          disabled={commMutation.isPending || Object.keys(waForm).length === 0}
                          onClick={() => commMutation.mutate({ whatsapp: waForm })}
                        >
                          {t('save')}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
        )}

        {/* Scheduled Reports */}
        {canToggle && (
        <Card className="animate-fade-in-up stagger-5 hover:shadow-glow-sm transition-all duration-200 xl:col-span-2">
          <CardHeader>
            <div className="flex items-start justify-between gap-4">
              <div>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Mail className="h-5 w-5 text-primary-text" />
                  {t('schedTitle')}
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">{t('schedSubtitle')}</p>
              </div>
              <Button
                size="sm"
                disabled={reportScheduleMutation.isPending}
                onClick={() => reportScheduleMutation.mutate(localSchedules)}
              >
                {reportScheduleMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin me-1" /> : null}
                {t('schedSaveBtn')}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {reportSchedulesLoading ? (
              <div className="space-y-3">
                {[1,2,3].map(i => <div key={i} className="h-20 bg-muted animate-pulse rounded-lg" />)}
              </div>
            ) : (
              <div className="space-y-4">
                {(localSchedules ?? []).map((schedule) => (
                  <div key={schedule.id} className="border border-border rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id={`sched-${schedule.id}`}
                          checked={schedule.enabled}
                          onChange={(e) => updateLocalSchedule(schedule.id, { enabled: e.target.checked })}
                        />
                        <Label htmlFor={`sched-${schedule.id}`} className="font-semibold cursor-pointer">
                          {t(`schedReport_${schedule.id.replace(/-/g, '_')}`, { defaultValue: schedule.label })}
                        </Label>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${schedule.enabled ? 'bg-primary/10 text-primary-text' : 'bg-muted text-muted-foreground'}`}>
                        {schedule.enabled ? t('schedActive') : t('schedInactive')}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div>
                        <Label className="text-xs">{t('schedFrequency')}</Label>
                        <Dropdown
                          value={schedule.frequency}
                          onChange={(e) => updateLocalSchedule(schedule.id, { frequency: e.value as ReportSchedule['frequency'] })}
                          options={[
                            { label: t('schedDaily'), value: 'daily' },
                            { label: t('schedWeekly'), value: 'weekly' },
                            { label: t('schedMonthly'), value: 'monthly' },
                          ]}
                          optionLabel="label"
                          optionValue="value"
                          className="mt-1 w-full"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">{t('schedSendAt')}</Label>
                        <Input
                          type="time"
                          value={schedule.sendAt ?? '08:00'}
                          onChange={(e) => updateLocalSchedule(schedule.id, { sendAt: e.target.value })}
                          className="mt-1 h-9 text-sm"
                        />
                      </div>
                      {schedule.frequency === 'weekly' && (
                        <div>
                          <Label className="text-xs">{t('schedDayOfWeek')}</Label>
                          <Dropdown
                            value={schedule.dayOfWeek ?? 1}
                            onChange={(e) => updateLocalSchedule(schedule.id, { dayOfWeek: Number(e.value) })}
                            options={[t('schedDay0'), t('schedDay1'), t('schedDay2'), t('schedDay3'), t('schedDay4'), t('schedDay5'), t('schedDay6')].map((d, i) => ({ label: d, value: i }))}
                            optionLabel="label"
                            optionValue="value"
                            className="mt-1 w-full"
                          />
                        </div>
                      )}
                      {schedule.frequency === 'monthly' && (
                        <div>
                          <Label className="text-xs">{t('schedDayOfMonth')}</Label>
                          <Input
                            type="number"
                            min={1} max={28}
                            value={schedule.dayOfMonth ?? 1}
                            onChange={(e) => updateLocalSchedule(schedule.id, { dayOfMonth: Number(e.target.value) })}
                            className="mt-1 h-9 text-sm"
                          />
                        </div>
                      )}
                      <div className="md:col-span-2">
                        <Label className="text-xs">{t('schedRecipients')}</Label>
                        <Input
                          type="text"
                          placeholder="admin@store.com, owner@store.com"
                          value={(schedule.recipients ?? []).join(', ')}
                          onChange={(e) => updateLocalSchedule(schedule.id, {
                            recipients: e.target.value.split(',').map((s: string) => s.trim()).filter(Boolean),
                          })}
                          className="mt-1 h-9 text-sm"
                        />
                      </div>
                    </div>
                    {schedule.lastSentAt && (
                      <p className="text-xs text-muted-foreground">
                        {t('schedLastSent')} {new Date(schedule.lastSentAt).toLocaleString('en-PK')}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        )}
        </>)}

        {settingsTab === 'integrations' && (<>
        {/* Currencies */}
        {canToggle && (
        <Card className="animate-fade-in-up stagger-5 hover:shadow-glow-sm transition-all duration-200 xl:col-span-2"><CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <DollarSign className="h-5 w-5 text-success-text" />
              {t('currenciesTitle')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <CurrenciesPage />
          </CardContent>
        </Card>
        )}

        {/* API Keys */}
        {(hasPermission('api:read') || hasPermission('api:*')) && (
        <Card className="animate-fade-in-up stagger-5 hover:shadow-glow-sm transition-all duration-200 xl:col-span-2">
          <CardHeader>
            <div className="flex items-start justify-between gap-4">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Key className="h-5 w-5 text-primary-text" />
                {t('apiKeysCardTitle')}
              </CardTitle>
              {canManageApiKeys && (
                <Button size="sm" className="gap-2" onClick={() => setAddApiKeyOpen(true)}>
                  <Key className="h-4 w-4" /> {t('newKeyBtn')}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {t('apiKeysDesc')}
            </p>

            {newApiKey && (
              <div className="rounded-xl border border-success/30 bg-success/10 p-4">
                <p className="text-sm font-semibold text-success-text mb-2">
                  {t('apiKeyCreatedMsg')}
                </p>
                <div className="flex items-center gap-2 bg-background rounded-lg border px-3 py-2">
                  <code className="text-xs font-mono flex-1 break-all">{newApiKey}</code>
                  <Button size="sm" variant="ghost" className="h-7 shrink-0" onClick={() => { navigator.clipboard.writeText(newApiKey); toast.success(t('copiedToast')); }}>
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>
                <Button size="sm" variant="ghost" className="mt-2 text-muted-foreground" onClick={() => setNewApiKey(null)}>
                  <CheckCheck className="h-3.5 w-3.5 me-1" /> {t('copiedItLabel')}
                </Button>
              </div>
            )}

            {apiKeysLoading ? (
              <p className="text-sm text-muted-foreground">{t('loadingLabel')}</p>
            ) : apiKeys.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('noApiKeysYet')}</p>
            ) : (
              <div className="space-y-2">
                {apiKeys.map((k) => (
                  <div key={k.id} className={`flex items-center justify-between rounded-lg border px-4 py-3 ${k.isActive ? '' : 'opacity-50'}`}>
                    <div>
                      <p className="text-sm font-medium">{k.name}</p>
                      {k.description && <p className="text-xs text-muted-foreground">{k.description}</p>}
                      <p className="text-xs font-mono text-muted-foreground mt-0.5">{k.keyPrefix}••••</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${k.isActive ? 'bg-success/10 text-success-text' : 'bg-muted text-muted-foreground'}`}>
                        {k.isActive ? t('activeStatus') : t('revokedStatus')}
                      </span>
                      {canManageApiKeys && k.isActive && (
                        <Button size="sm" variant="outline" className="h-7 text-xs text-destructive-text border-destructive/30 hover:bg-destructive/10"
                          onClick={() => setRevokeApiKeyId(k.id)}>
                          {t('revokeBtn')}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {/* Webhooks */}
        {(hasPermission('api:read') || hasPermission('api:*')) && (
        <Card className="animate-fade-in-up stagger-5 hover:shadow-glow-sm transition-all duration-200 xl:col-span-2">
          <CardHeader>
            <div className="flex items-start justify-between gap-4">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Webhook className="h-5 w-5 text-primary-text" />
                {t('whTitle')}
              </CardTitle>
              {canManageWebhooks && (
                <Button size="sm" className="gap-2" onClick={() => setAddWebhookOpen(true)}>
                  <Plus className="h-4 w-4" /> {t('whAddBtn')}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {t('whIntro')}
            </p>
            {newWebhookSecret && (
              <div className="rounded-lg border border-warning/40 bg-warning/10 p-3 space-y-2">
                <p className="text-xs font-medium text-warning-text">{t('whCopySecret')}</p>
                <div className="flex items-center gap-2">
                  <code className="text-xs font-mono flex-1 break-all">{newWebhookSecret}</code>
                  <Button size="sm" variant="ghost" className="h-7 shrink-0" onClick={() => { navigator.clipboard.writeText(newWebhookSecret); toast.success(t('copiedToast')); }}>
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>
                <Button size="sm" variant="ghost" className="mt-1 text-muted-foreground" onClick={() => setNewWebhookSecret(null)}>
                  {t('whDismiss')}
                </Button>
              </div>
            )}
            {webhooksLoading ? (
              <div className="space-y-2">
                {[1,2].map(i => <div key={i} className="h-14 rounded-lg bg-muted animate-pulse" />)}
              </div>
            ) : webhooks.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('whNone')}</p>
            ) : (
              <div className="space-y-2">
                {webhooks.map((wh) => (
                  <div key={wh.id} className="flex items-start justify-between rounded-lg border p-3 gap-4">
                    <div className="min-w-0 flex-1 space-y-1">
                      <p className="text-sm font-medium truncate">{wh.name}</p>
                      <p className="text-xs text-muted-foreground font-mono truncate">{wh.url}</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {(wh.events as string[]).map((evt) => (
                          <span key={evt} className="text-[10px] bg-muted px-1.5 py-0.5 rounded font-mono">{evt}</span>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${wh.isActive ? 'bg-success/10 text-success-text' : 'bg-muted text-muted-foreground'}`}>
                        {wh.isActive ? t('schedActive') : t('whPaused')}
                      </span>
                      {canManageWebhooks && (
                        <Button variant="ghost" size="sm" className="h-7 text-destructive-text" onClick={() => setDeleteWebhookId(wh.id)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {/* My Plan & Features */}
        <Card className="animate-fade-in-up stagger-5 hover:shadow-glow-sm transition-all duration-200 xl:col-span-2">

        {/* ── Receipt Printer (ESC/POS) ── */}
        <Card className="animate-fade-in-up stagger-5 hover:shadow-glow-sm transition-all duration-200 xl:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Receipt className="h-5 w-5 text-info-text" />
              {t('prnTitle')}
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              {t('prnIntro')}
            </p>
            <p className="text-xs text-muted-foreground">{t('printerDeviceOnly')}</p>
          </CardHeader>
          <CardContent className="space-y-5">
            {!printerSupported && (
              <div className="flex items-center gap-2 rounded-lg border border-warning/30 bg-warning/10 px-4 py-3">
                <AlertCircle className="h-4 w-4 text-warning-text shrink-0" />
                <p className="text-sm text-warning-text">{t('prnNoWebUsb')}</p>
              </div>
            )}

            {/* Connection status */}
            <div className="flex items-center justify-between gap-4 rounded-xl border p-4">
              <div className="flex items-center gap-3">
                <div className={`h-2.5 w-2.5 rounded-full ${printerConnected ? 'bg-success' : 'bg-muted-foreground/40'}`} />
                <div>
                  <p className="text-sm font-medium">{printerConnected ? printerDeviceName ?? t('prnConnected') : t('prnNone')}</p>
                  <p className="text-xs text-muted-foreground">{printerConnected ? t('prnReady') : t('prnConnectHint')}</p>
                </div>
              </div>
              <div className="flex gap-2">
                {printerConnected ? (
                  <>
                    <Button size="sm" variant="outline" onClick={handlePrinterTest} disabled={printerTesting || !printerSupported}>
                      {printerTesting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : t('prnTestPrint')}
                    </Button>
                    <Button size="sm" variant="destructive" onClick={handlePrinterDisconnect}>{t('prnDisconnect')}</Button>
                  </>
                ) : (
                  <Button size="sm" onClick={handlePrinterConnect} disabled={printerConnecting || !printerSupported}>
                    {printerConnecting ? <Loader2 className="h-3.5 w-3.5 animate-spin me-2" /> : null}
                    {t('prnConnectBtn')}
                  </Button>
                )}
              </div>
            </div>

            {/* Printer settings */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="mb-2 block">{t('prnPaperWidth')}</Label>
                <div className="flex gap-2">
                  {([58, 80] as const).map((w) => (
                    <button
                      key={w}
                      onClick={() => savePrinterSettings({ paperWidth: w })}
                      className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                        printerSettings.paperWidth === w
                          ? 'border-primary bg-primary/10 text-primary-text'
                          : 'border-border hover:bg-muted'
                      }`}
                    >{w}mm</button>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm">{t('prnAutoCut')}</Label>
                  <button
                    onClick={() => savePrinterSettings({ cutPaper: !printerSettings.cutPaper })}
                    className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${printerSettings.cutPaper ? 'bg-primary' : 'bg-muted-foreground/30'}`}
                  >
                    <span className={`pointer-events-none inline-block h-4 w-4 translate-y-[-0.5px] rounded-full bg-background shadow-lg transition-transform ${printerSettings.cutPaper ? 'translate-x-4' : 'translate-x-0'}`} />
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <Label className="text-sm">{t('prnOpenDrawer')}</Label>
                  <button
                    onClick={() => savePrinterSettings({ openDrawer: !printerSettings.openDrawer })}
                    className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${printerSettings.openDrawer ? 'bg-primary' : 'bg-muted-foreground/30'}`}
                  >
                    <span className={`pointer-events-none inline-block h-4 w-4 translate-y-[-0.5px] rounded-full bg-background shadow-lg transition-transform ${printerSettings.openDrawer ? 'translate-x-4' : 'translate-x-0'}`} />
                  </button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
          <CardHeader>
            <div className="flex items-start justify-between gap-4">
              <div>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Zap className="h-5 w-5 text-warning-text" />
                  {t('myPlanTitle')}
                </CardTitle>
                <p className="mt-1 text-sm text-muted-foreground">
                  {t('currentPlanLabel')} <span className="font-semibold text-foreground">{planName}</span>
                  {' '}<Trans
                    t={t}
                    i18nKey="myPlanActiveSentence"
                    components={{ marker: <span className="inline-flex items-center gap-1 text-success-text font-medium"><CheckCircle2 className="h-3.5 w-3.5" />{t('featuresEnabledLabel')}</span> }}
                  />
                </p>
              </div>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                <Button size="sm" className="shrink-0 gap-2 bg-success-solid hover:bg-success-solid/90 text-success-solid-foreground">
                  <MessageCircle className="h-4 w-4" />
                  {t('upgradeWhatsApp')}
                </Button>
              </a>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {ALL_FEATURES.map((feature) => {
                const Icon = feature.icon;
                const enabled = feature.permission
                  ? hasPermission(feature.permission as string)
                  : activeAddonKeys.includes(('addonKey' in feature ? feature.addonKey : ''));
                return (
                  <div
                    key={feature.key}
                    className={`flex items-start gap-3 rounded-xl border p-3.5 transition-colors ${
                      enabled
                        ? 'border-success/30 bg-success/5'
                        : 'border-border bg-muted/20 opacity-60'
                    }`}
                  >
                    <div className={`mt-0.5 rounded-lg p-1.5 ${enabled ? 'bg-success/10' : 'bg-muted'}`}>
                      {enabled
                        ? <Icon className="h-4 w-4 text-success-text" />
                        : <Lock className="h-4 w-4 text-muted-foreground" />
                      }
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <p className="text-sm font-medium leading-none">{t(`feature_${feature.key}_name`)}</p>
                        {enabled
                          ? <CheckCircle2 className="h-3.5 w-3.5 text-success-text shrink-0" />
                          : <span className="text-xs text-muted-foreground">{t('lockedLabel')}</span>
                        }
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground leading-relaxed">{t(`feature_${feature.key}_desc`)}</p>
                      {!enabled && (
                        <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="mt-1.5 inline-flex items-center gap-0.5 text-xs font-medium text-success-text hover:underline">
                          {t('unlockFeature')} <ChevronRight className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-4 flex items-center justify-between rounded-xl border border-success/30 bg-success/10 px-4 py-3">
              <div>
                <p className="text-sm font-medium text-success-text">{t('wantToUnlock')}</p>
                <p className="text-xs text-success-text/70 mt-0.5">{t('chatWhatsAppInfo')}</p>
              </div>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                <Button size="sm" className="gap-2 bg-success-solid hover:bg-success-solid/90 text-success-solid-foreground">
                  <MessageCircle className="h-4 w-4" />
                  {t('chatNow')}
                </Button>
              </a>
            </div>
          </CardContent>
        </Card>

        {/* ── Data Management ── */}
        {/* ── Payment Terminal SDK ── */}
        {canToggle && (
        <TerminalSettingsCard />
        )}

        {canToggle && (
        <Card className="animate-fade-in-up stagger-5 hover:shadow-glow-sm transition-all duration-200 xl:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <HardDrive className="h-5 w-5 text-success-text" />
                {t('dataMgmtTitle')}
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                className="gap-1.5 text-muted-foreground"
                onClick={() => setShowDataSection((v) => !v)}
              >
                {showDataSection ? t('dataMgmtHide') : t('dataMgmtShow')}
                <ChevronDown className={`h-4 w-4 transition-transform ${showDataSection ? 'rotate-180' : ''}`} />
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              {t('dataMgmtIntro')}
            </p>
          </CardHeader>

          {showDataSection && (
          <CardContent className="space-y-4">
            <TabBar
              bordered={false}
              tabs={[
                { key: 'stats', label: t('dataTabStats'), icon: TableIcon },
                { key: 'backup', label: t('dataTabBackup'), icon: Download },
                { key: 'restore', label: t('dataTabRestore'), icon: Upload },
              ]}
              activeTab={dataTab}
              onTabChange={(key) => setDataTab(key as typeof dataTab)}
            />

            {/* ── Stats Tab ── */}
            {dataTab === 'stats' && (
              <div className="space-y-3">
                {statsLoading ? (
                  <div className="flex items-center justify-center py-10 gap-2 text-muted-foreground">
                    <Loader2 className="h-5 w-5 animate-spin" /> {t('dataLoadingStats')}
                  </div>
                ) : (
                  <>
                    {/* Summary */}
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        {
                          label: t('dataStatRows'),
                          value: dbStats.reduce((s, r) => s + r.tenantRows, 0).toLocaleString(),
                        },
                        {
                          label: t('dataStatStorage'),
                          value: fmtBytes(dbStats.reduce((s, r) => s + r.estimatedTenantBytes, 0)),
                        },
                        {
                          label: t('dataStatTables'),
                          value: dbStats.filter((r) => r.tenantRows > 0).length.toString(),
                        },
                      ].map((stat) => (
                        <StatCard key={stat.label} label={stat.label} value={stat.value} format="raw" />
                      ))}
                    </div>

                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <AlertCircle className="h-3 w-3 shrink-0" />
                      Storage is shared across all tenants on this database. "Est. your storage" is proportional based on your row count.
                    </p>

                    {/* Table */}
                    <div className="rounded-lg border overflow-hidden">
                      <div className="overflow-y-auto max-h-80">
                        <table className="w-full text-sm">
                          <thead className="sticky top-0 bg-muted/80 backdrop-blur-sm">
                            <tr>
                              <th className="px-4 py-2.5 text-start text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('dataColTable')}</th>
                              <th className="px-4 py-2.5 text-end text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('dataColRows')}</th>
                              <th className="px-4 py-2.5 text-end text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('dataColSize')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {dbStats.filter((r) => r.tenantRows > 0).map((row) => (
                              <tr key={row.tableName} className="border-t hover:bg-accent/40 transition-colors">
                                <td className="px-4 py-2">
                                  <p className="font-medium text-xs">{row.label}</p>
                                  <p className="text-[10px] text-muted-foreground font-mono">{row.tableName}</p>
                                </td>
                                <td className="px-4 py-2 text-end font-mono text-xs font-semibold text-success-text">
                                  {row.tenantRows.toLocaleString()}
                                </td>
                                <td className="px-4 py-2 text-end font-mono text-xs text-muted-foreground">
                                  {row.estimatedTenantSizePretty}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ── Backup Tab ── */}
            {dataTab === 'backup' && (
              <div className="space-y-4">
                <div className="rounded-lg border border-info/30 bg-info/10 p-4">
                  <div className="flex items-center gap-2 text-info-text mb-2">
                    <Download className="h-4 w-4" />
                    <p className="font-semibold text-sm">{t('dataIncluded')}</p>
                  </div>
                  <ul className="text-xs text-info-text space-y-1 ms-6 list-disc">
                    <li>{t('dataInc1')}</li>
                    <li>{t('dataInc2')}</li>
                    <li>{t('dataInc3')}</li>
                    <li>{t('dataInc4')}</li>
                  </ul>
                </div>

                <div className="flex flex-col items-center gap-4 py-6 rounded-lg border-2 border-dashed border-border bg-muted/20">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-success to-success text-success-foreground shadow-lg">
                    <Database className="h-7 w-7" />
                  </div>
                  <div className="text-center">
                    <p className="font-semibold">{tenant?.name}</p>
                    <p className="text-sm text-muted-foreground">{t('dataFullExport')}</p>
                  </div>
                  <Button
                    onClick={handleDownloadBackup}
                    disabled={downloadingBackup}
                    className="gap-2 bg-success-solid hover:bg-success-solid/90 text-success-solid-foreground"
                  >
                    {downloadingBackup ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                    {downloadingBackup ? t('dataPreparing') : t('dataDownloadBackup')}
                  </Button>
                </div>
              </div>
            )}

            {/* ── Restore Tab ── */}
            {dataTab === 'restore' && (
              <div className="space-y-4">
                <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4">
                  <div className="flex items-center gap-2 text-destructive-text mb-2">
                    <AlertCircle className="h-4 w-4" />
                    <p className="font-semibold text-sm">{t('dataReplaceWarn')}</p>
                  </div>
                  <ul className="text-xs text-destructive-text space-y-1 ms-6 list-disc">
                    <li>{t('dataWarn1')}</li>
                    <li>{t('dataWarn2')}</li>
                    <li>{t('dataWarn3')}</li>
                    <li>{t('dataWarn4')}</li>
                  </ul>
                </div>

                {restoreResult ? (
                  <div className="rounded-lg border border-success/30 bg-success/10 p-4 space-y-3">
                    <div className="flex items-center gap-2 text-success-text">
                      <CheckCircle2 className="h-5 w-5" />
                      <p className="font-semibold">{t('dataRestoreDone')}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                      {Object.entries(restoreResult.restored)
                        .filter(([, count]) => (count as number) > 0)
                        .map(([model, count]) => (
                          <div key={model} className="flex justify-between text-xs bg-success/10 rounded px-2 py-1">
                            <span className="font-mono">{model}</span>
                            <span className="font-semibold text-success-text">{count as number} {t('dataRowsSuffix')}</span>
                          </div>
                        ))}
                    </div>
                    {restoreResult.recoveryPoint && (
                      <div className="rounded-md border border-success/30 bg-success/5 px-3 py-2 text-xs text-success-text">
                        <span className="font-semibold">{t('recoveryPointCaptured')}</span>{' '}
                        {new Date(restoreResult.recoveryPoint.capturedAt).toLocaleString()} —{' '}
                        {t('recoveryPointSaved', { n: restoreResult.recoveryPoint.totalDeleted.toLocaleString() })}
                      </div>
                    )}
                    {restoreResult.skipped.length > 0 && (
                      <p className="text-xs text-warning-text">{t('dataSkipped')} {restoreResult.skipped.join(', ')}</p>
                    )}
                    <Button variant="outline" size="sm" onClick={() => setRestoreResult(null)} className="w-full">
                      {t('dataRestoreAnother')}
                    </Button>
                  </div>
                ) : (
                  <>
                    <div
                      className="flex flex-col items-center gap-3 py-8 rounded-lg border-2 border-dashed border-border bg-muted/20 cursor-pointer hover:bg-muted/40 transition-colors"
                      onClick={() => restoreFileInputRef.current?.click()}
                    >
                      <Upload className="h-10 w-10 text-muted-foreground" />
                      <div className="text-center">
                        <p className="font-medium text-sm">
                          {restoreFile ? restoreFile.name : t('dataPickFile')}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {restoreFile ? fmtBytes(restoreFile.size) : t('dataJsonFile')}
                        </p>
                      </div>
                      <input
                        ref={restoreFileInputRef}
                        type="file"
                        accept=".json,application/json"
                        className="hidden"
                        onChange={(e) => { setRestoreFile(e.target.files?.[0] ?? null); setRestoreResult(null); }}
                      />
                    </div>
                    {restoreFile && (
                      <Button
                        variant="destructive"
                        className="w-full gap-2"
                        disabled={restoreMutation.isPending || preparingRestore}
                        onClick={() => prepareRestore(restoreFile)}
                      >
                        {(restoreMutation.isPending || preparingRestore) ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                        {preparingRestore ? t('dataAnalyzing') : restoreMutation.isPending ? t('dataRestoring') : t('dataRestoreFromBackup')}
                      </Button>
                    )}
                  </>
                )}
              </div>
            )}
          </CardContent>
          )}
        </Card>
        )}
        </>)}
      </div>
    </div>

    {/* POS-I224: leaving Settings with edited, unsaved fields asks first */}
    <ConfirmDialog
      open={leaveGuard.open}
      variant="warning"
      title={t('leaveUnsavedTitle')}
      message={t('leaveUnsavedMsg')}
      confirmLabel={t('leaveUnsavedConfirm')}
      cancelLabel={t('leaveUnsavedStay')}
      onConfirm={leaveGuard.confirmLeave}
      onCancel={leaveGuard.stay}
    />

    {/* ── Restore Confirm ── */}
    <ConfirmDialog
      open={restoreConfirm}
      title={t('dataConfirmTitle')}
      message={
        restorePreview
          ? `Impact: this will permanently DELETE ${restorePreview.totalCurrent.toLocaleString()} existing record(s) for "${restorePreview.tenantName}" and insert ${restorePreview.totalIncoming.toLocaleString()} record(s) from the backup${restorePreview.backupExportedAt ? ` (exported ${new Date(restorePreview.backupExportedAt).toLocaleString()})` : ''}. A safety backup of your current data will be downloaded automatically first (recovery point). User accounts are NOT affected. This cannot be undone.`
          : `This will permanently DELETE all business data for "${tenant?.name}" and replace it with the selected backup. User accounts will NOT be affected. This cannot be undone.`
      }
      confirmLabel={t('dataConfirmBtn')}
      variant="danger"
      loading={restoreMutation.isPending || downloadingBackup}
      onConfirm={() => restoreFile && handleRestoreFile(restoreFile)}
      onCancel={() => { setRestoreConfirm(false); setRestorePreview(null); }}
    />

    <ConfirmDialog
      open={confirmTestMode}
      onCancel={() => setConfirmTestMode(false)}
      onConfirm={() => {
        // POS-I113: one click is enough; the dialog stays open with a spinner
        // until the switch settles, so the click visibly does something and a
        // second click cannot send the toggle twice.
        if (toggleMutation.isPending) return;
        toggleMutation.mutate(!isTestMode, { onSettled: () => setConfirmTestMode(false) });
      }}
      loading={toggleMutation.isPending}
      // POS-I113: these keys must exist in locales/{en,ur,ro}/settings.json
      // (pinned by settings-i18n-keys.spec.ts); the old names were never defined.
      title={isTestMode ? t('switchToProductionTitle') : t('switchToTestModeTitle')}
      message={isTestMode ? t('switchToProductionMsg') : t('switchToTestModeMsg')}
      confirmLabel={isTestMode ? t('switchToProduction') : t('confirmSwitchToTestMode')}
      variant={isTestMode ? 'warning' : 'danger'}
    />
    <ConfirmDialog
      open={confirmReset}
      onCancel={() => setConfirmReset(false)}
      onConfirm={() => {
        resetTestDataMutation.mutate();
        setConfirmReset(false);
      }}
      title={t('clearAllTestDataTitle')}
      message={t('clearAllTestDataMsg')}
      confirmLabel={t('yesClearTestData')}
      variant="danger"
    />

    {/* Send Test SMS dialog */}
    {smsTestOpen && (
      <div className="fixed inset-0 z-50 bg-scrim/50 flex items-center justify-center p-4">
        <div className="bg-background rounded-xl shadow-xl w-full max-w-sm p-6 space-y-4">
          <h2 className="text-lg font-semibold flex items-center gap-2"><Bell className="h-5 w-5" /> {t('testSms')}</h2>
          <div>
            <Label className="text-xs">{t('phoneNumberLabel')}</Label>
            <Input
              dir="ltr"
              value={testSmsPhone}
              onChange={(e) => setTestSmsPhone(e.target.value)}
              placeholder="+923001234567"
            />
            <p className="text-xs text-muted-foreground mt-1">{t('smsPhoneHint')}</p>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setSmsTestOpen(false)}>{t('cancel')}</Button>
            <Button onClick={sendTestSms} disabled={sendingSms}>
              {sendingSms ? t('sendingLabel') : t('sendSmsBtn')}
            </Button>
          </div>
        </div>
      </div>
    )}

    {/* Add API Key dialog */}
    {addApiKeyOpen && (
      <div className="fixed inset-0 z-50 bg-scrim/50 flex items-center justify-center p-4">
        <div className="bg-background rounded-xl shadow-xl w-full max-w-sm p-6 space-y-4">
          <h2 className="text-lg font-semibold flex items-center gap-2"><Key className="h-5 w-5" /> {t('createApiKeyTitle')}</h2>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">{t('nameRequiredLabel')}</Label>
              <Input maxLength={80} value={apiKeyForm.name} onChange={(e) => setApiKeyForm({ ...apiKeyForm, name: e.target.value })} placeholder={t('apiKeyNamePlaceholder')} />
            </div>
            <div>
              <Label className="text-xs">{t('descriptionLabel')}</Label>
              <Input value={apiKeyForm.description} onChange={(e) => setApiKeyForm({ ...apiKeyForm, description: e.target.value })} placeholder={t('apiKeyDescPlaceholder')} />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setAddApiKeyOpen(false)}>{t('cancel')}</Button>
            <Button
              onClick={() => {
                if (!apiKeyForm.name.trim()) { toast.error(t('nameIsRequired')); return; }
                createApiKeyMutation.mutate({ name: apiKeyForm.name.trim(), description: apiKeyForm.description.trim() || undefined });
                setAddApiKeyOpen(false);
              }}
              disabled={createApiKeyMutation.isPending}
            >
              {t('createKeyBtn')}
            </Button>
          </div>
        </div>
      </div>
    )}

    <ConfirmDialog
      open={!!revokeApiKeyId}
      title={t('revokeApiKeyTitle')}
      message={t('revokeApiKeyMsg')}
      confirmLabel={t('revokeKeyBtn')}
      variant="danger"
      onConfirm={() => revokeApiKeyId && revokeApiKeyMutation.mutate(revokeApiKeyId)}
      onCancel={() => setRevokeApiKeyId(null)}
    />

    {/* Add Webhook dialog */}
    {addWebhookOpen && (
      <div className="fixed inset-0 z-50 bg-scrim/50 flex items-center justify-center p-4">
        <div className="bg-background rounded-xl shadow-xl w-full max-w-md p-6 space-y-4">
          <h2 className="text-lg font-semibold flex items-center gap-2"><Webhook className="h-5 w-5" /> {t('whAddBtn')}</h2>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">{t('whNameRequired')}</Label>
              <Input value={webhookForm.name} onChange={(e) => setWebhookForm({ ...webhookForm, name: e.target.value })} placeholder={t('whNamePlaceholder')} />
            </div>
            <div>
              <Label className="text-xs">{t('whUrlRequired')}</Label>
              <Input value={webhookForm.url} onChange={(e) => setWebhookForm({ ...webhookForm, url: e.target.value })} placeholder="https://your-server.com/webhook" />
            </div>
            <div>
              <Label className="text-xs">{t('whEventsLabel')}</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {WEBHOOK_EVENTS.map((evt) => (
                  <button
                    key={evt}
                    type="button"
                    onClick={() => toggleWebhookEventFilter(evt)}
                    className={`text-xs px-2 py-1 rounded-full border font-mono transition-colors ${
                      webhookForm.events.includes(evt)
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-muted text-muted-foreground border-border hover:border-primary'
                    }`}
                  >
                    {evt}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setAddWebhookOpen(false)}>{t('cancel')}</Button>
            <Button
              onClick={() => {
                if (!webhookForm.name.trim()) { toast.error(t('whNameIsRequired')); return; }
                if (!webhookForm.url.trim()) { toast.error(t('whUrlIsRequired')); return; }
                if (webhookForm.events.length === 0) { toast.error(t('whSelectEvent')); return; }
                createWebhookMutation.mutate({ name: webhookForm.name.trim(), url: webhookForm.url.trim(), events: webhookForm.events });
              }}
              disabled={createWebhookMutation.isPending}
            >
              {t('whCreateBtn')}
            </Button>
          </div>
        </div>
      </div>
    )}

    <ConfirmDialog
      open={!!deleteWebhookId}
      title={t('whDeleteTitle')}
      message={t('whDeleteMsg')}
      confirmLabel={t('whDeleteConfirm')}
      variant="danger"
      onConfirm={() => deleteWebhookId && deleteWebhookMutation.mutate(deleteWebhookId)}
      onCancel={() => setDeleteWebhookId(null)}
    />
    </>
  );
}

